<template>
  <v-container fluid>
    <SharedTableSkeleton
      v-if="showSkeletonLoading"
      :row-count="pagination.perPage || 10"
      :column-count="4"
      column-template="2fr 1fr 1fr 80px"
    />
    <div v-if="!dataLoading" class="d-flex flex-column employee-card-path-to-readiness">
      <EmployeePageHeader
        v-if="!hideHeader"
        :key="
          employeeData && employeeData.id
            ? employeeData.id +
              '-' +
              (employeeData.primary_positions ? employeeData.primary_positions.length : 0) +
              '-' +
              currentEmployeeStatus
            : 'default'
        "
        :status="currentEmployeeStatus"
        :name="employeeFullName"
        :avatar="employeeData?.avatar || ''"
        :title="employeeData?.title || ''"
        :archived="isArchived"
        :tracking="isTracking"
        :readiness="employeeData?.current_readiness || 0"
        :last_month_readiness="lastMonthReadiness"
        :breadcrumbs="breadcrumbsList"
        :employeeId="employeeData?.id"
        :employeeData="employeeData"
        :readinessRecalc="{ pending: readinessIsPending, failed: readinessHasFailed }"
        @employeeUpdated="handleEmployeeUpdate"
        @employeeStatusChanged="handleEmployeeStatusChanged"
        @employeeDeleted="handleEmployeeDeleted"
      />

      <div v-if="isRecalculating" class="readiness-recalc-status" role="status" aria-live="polite">
        <v-progress-circular
          indeterminate
          color="primary"
          :size="18"
          :width="2"
          class="readiness-recalc-status__spinner"
        />
        <span class="readiness-recalc-status__text">
          Recalculating readiness — this can take up to a minute or two. Your updated readiness will
          appear here automatically once the calculation finishes.
        </span>
      </div>

      <div
        v-if="!dataLoading"
        v-show="!inlineInspectOpen"
        class="custom-path path-to-readiness-scrollable"
      >
        <div v-if="dataSource.length === 0 && employeeData">
          <NoRecordFound
            :title="''"
            :subtitle="''"
            :showSpecificMessage="title"
            message=""
            class="no-record-found"
            :shouldShowAddAction="true"
            :noBorder="true"
            :showActiveCounter="false"
            :showClearAction="false"
            :btnTitle="btnTitle"
            :addPrefix="false"
            :description="description"
            :plusIcon="false"
            @onAction="handleButtonClick"
          />
        </div>
        <PathToReadinessTreeList
          v-else
          ref="PathToReadinessTreeList"
          :key="treeListKey"
          :dataSource="dataSource"
          :topLevel="topLevel"
          :removeBorder="removeBorder"
          :dropdownItems="dropdownItems"
          @optionClick="onOptionClick"
          @treeExpandRole="onTreeExpandRole"
          :showRespBadge="true"
          :showAvailableCoaches="true"
          @loadData="reloadDataAfterChanges()"
          @importanceChanged="onImportanceChanged"
          :loading="loading.initial"
          :treeMode="true"
          :showDropDown="false"
          :expandedKeys="expandedKeys"
          @onExpansionStateChange="handleExpansionStateChange"
          @loadDataAfterBadgeOperation="reloadDataAfterBadgeOperation()"
          @chatter-click="openChatterDrawer($event.data)"
          :read_only="read_only"
          :hideInspect="hideInspectActions"
          :pendingReadinessIds="readinessPendingIds"
          :failedReadinessIds="readinessFailedIds"
        />
        <ExPagination
          v-if="dataSource.length > 0 && pagination.total > pagination.perPage"
          :pagination="pagination"
          @onRequest="handlePaginationRequest"
        />
      </div>
      <AsCoachInspectWorkspace
        v-if="inlineInspectEnabled"
        :inspect="inspectCtx"
        :breadcrumbs="inlineInspectBreadcrumbs"
        @breadcrumb-click="closeInlineInspect"
      />
      <GiveBadgeDialog
        :value="isForceBadge || showGiveBadgeDialogFromInspection"
        :description="giveBadgeDialogDescription"
        :employeeName="giveBadgeDialogEmployeeName"
        :responsibilityName="giveBadgeDialogResponsibilityName"
        :canNotify="giveBadgeDialogCanNotify"
        :defaultLevel="giveBadgeDialogDefaultLevel"
        :currentBadgeLevel="giveBadgeDialogCurrentLevel"
        :lockedLevel="giveBadgeDialogLockedLevel"
        :loading="loading.forceBadge"
        @input="onGiveBadgeDialogInput"
        @give-badge="onGiveBadgeFromDialog"
        @close="onEmployeeModalBack"
      />
      <UpgradeBadgeDialog
        :value="showUpgradeBadgeDialog"
        :employeeName="giveBadgeDialogEmployeeName"
        :responsibilityName="giveBadgeDialogResponsibilityName"
        :canNotify="giveBadgeDialogCanNotify"
        :currentBadgeLevel="upgradeBadgeDialogCurrentLevel"
        :loading="loading.upgradeBadge"
        @input="onUpgradeBadgeDialogInput"
        @give-badge="onGiveBadgeFromUpgradeDialog"
        @close="closeUpgradeBadgeModal"
      />
      <ChecklistInspectionRightPane
        :open="isInspectResModal"
        :employee="inspectionPaneEmployee"
        :readiness-chip="inspectionReadinessChip"
        :responsibilityAssignment="inspectionResponsibilityAssignment"
        :responsibilityCheckpoints="responsibilityCheckpoints"
        :items="checkpointsData"
        :award-dialog-request="inspectionAwardDialogRequest"
        :current-employee="inspectionAwardBadgeDialogCurrentEmployee"
        :award-eligibility-by-access-null="true"
        @close="onCloseInspectionPane"
        @removeBadge="processRemoveBadgeFromEvalModal"
        @issueBadge="handleIssueBadgeFromInspection"
        @onPass="handleCheckpointPass"
        @onFail="handleCheckpointFail"
        @onNotApplicable="handleCheckpointNotApplicable"
        @onClose="handleCheckpointClose"
        @badgeAwarded="handleBadgeAwarded"
        @expiration-synced="handleInspectionBadgeExpirationSynced"
      />
      <ReasonModal
        :showDialog="showReasonModal"
        :title="reasonModalType === 'fail' ? 'Fail Checkpoint' : 'Mark Not Applicable'"
        :description="
          reasonModalType === 'fail'
            ? 'Please provide a reason why this checkpoint was failed.'
            : 'Please provide a reason why this checkpoint is not applicable.'
        "
        :btnLabel="reasonModalType === 'fail' ? 'Fail' : 'Mark N/A'"
        :btnColor="reasonModalType === 'fail' ? '#D92D20' : '#504D56'"
        :btnDanger="reasonModalType === 'fail'"
        :value="reasonText"
        @input="reasonText = $event"
        @onAction="confirmCheckpointReason"
        @onClose="closeCheckpointReasonModal"
      />
      <EvalResponsibilityModal v-if="!inlineInspectEnabled" />
      <ActivateEmployee
        :show="activateEmployee"
        :employee="currenntEmployee"
        @onClose="onCloseModal"
        @onActiveEmployee="handleActivateEmployee"
      ></ActivateEmployee>
      <ReActivateEmployee
        :show="reActivateEmployee"
        :employee="currenntEmployee"
        @onClose="onCloseModal"
        @onActiveEmployee="reactivateEmployee"
      ></ReActivateEmployee>

      <AssignEmployeeToPrimaryPositionDialog
        :value="assignPosition"
        :employeeName="employeeFullName"
        :employeeFirstName="employeeData?.first_name || ''"
        :employeeLastName="employeeData?.last_name || ''"
        :employeeAvatar="employeeData?.avatar || ''"
        :items="primaryPositions.availablePositions"
        :hasMoreData="primaryPositions.hasMoreData"
        :isLoadingMore="primaryPositions.isLoadingMore"
        :noResults="primaryPositions.noResults"
        :loading="loading.employeeAssignment"
        :canNotifyEmployee="!!employeeData?.email"
        :equalVacancy="equalVacancyPicker"
        :employeeAvailability="primaryPositions.employeeVacancy"
        :rejectionMessage="assignRejectionMessage"
        @input="assignPosition = $event"
        @date-change="onAssignDateChange"
        @assign="onAssignPositionToEmployee"
        @search="searchPrimaryPositions"
        @load-more="loadMorePrimaryPositions"
      />
      <RetireResponsibilityCatelog
        v-model="showRetireResponsibilityDialog"
        :responsibility="
          currentResponsibility && currentResponsibility.name ? currentResponsibility.name : ''
        "
        @onSave="retireResponsibility"
      />
      <BadgeConfirmationDialog
        :isVisible="showBadgeConfirmationDialog"
        :badgeData="badgeData"
        :badgeType="
          badgeData && badgeData.payload && badgeData.payload.issued_method == 'given'
            ? 'Give'
            : 'Award'
        "
        :responsibilityName="
          currentResponsibility && currentResponsibility.name ? currentResponsibility.name : ''
        "
        :employee="{
          name: employeeData ? `${employeeData.first_name} ${employeeData.last_name}` : '',
          avatar: employeeData && employeeData.photoUrl,
        }"
        :level="getBadgeLevel(badgeData && badgeData.payload ? badgeData.payload.level : null)"
        @close="showBadgeConfirmationDialog = false"
        :currentEmployee="$store.getters['employeeStore/currentEmployee']"
        @keydown="onBadgeConfirmationKeydown"
        @onClick="onClick"
      />
      <AwardBadgeDialog
        :employee="awardBadgeDialogEmployee"
        :level="awardBadgeLevel"
        :isVisible="showAwardBadgeModal"
        @skip="showAwardBadgeModal = false"
        @close="showAwardBadgeModal = false"
        @activate="awardBadge"
        :currentEmployee="awardBadgeDialogCurrentEmployee"
        :eligibility-by-access-null="true"
        :responsibilityName="awardBadgeResponsibilityName"
      />
      <ChatterDrawer
        :drawer="showChatterDrawer"
        :type="
          chatterType === 'badge'
            ? 'Badge'
            : chatterType === 'responsibility'
            ? 'Responsibility'
            : 'Badge'
        "
        :typeValue="chatterTypeValue || ''"
        :listData="selectedBadgeChatter"
        :object-type="chatterType || 'badge'"
        :object-id="selectedBadgeChatter?.id ?? null"
        @closeDrawer="closeChatterDrawer"
        :iconName="chatterIconName || 'org_icon_responsibility'"
        :chatterList="chatterData"
        :totalCheckpointChatter="totalChatter"
        @requestMoreCheckpointsChatter="loadMoreChatter"
      />
      <RemoveBadgeDialog
        v-model="showRemoveBadgeDialog"
        :badgeName="selectedBadgeForRemoval?.badgeName || ''"
        :responsibilityName="selectedBadgeForRemoval?.responsibilityName || ''"
        :employeeName="employeeFullName || ''"
        :loading="loading.removeBadge"
        @remove="handleConfirmRemoveBadge"
        @close="handleCloseRemoveBadgeDialog"
      />
    </div>
  </v-container>
</template>
<script>
import { getCurrentInstance, reactive } from "vue";
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import AsCoachInspectWorkspace from "@components/AsCoachInspectWorkspace/AsCoachInspectWorkspace.vue";
import { useCoachInspect } from "@components/AsCoachInspectWorkspace/composables/useCoachInspect";
import { buildInspectionRowFromOrganizationContext } from "@components/utils/buildInspectionRowFromOrganizationContext";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import {
  LD_FLAG_IDEA_MDP_560,
  LD_FLAG_IDEA_MDP_647,
  LD_FLAG_IDEA_MDP_651,
} from "@components/constants/launchDarklyFlagKeys";
import {
  fetchEmployeeVacancy,
  getAssignmentRejectionMessage,
  loadEqualVacancyPositions,
} from "@components/utils/primaryAssignmentPositions";
import PathToReadinessTreeList from "@components/DataTable/PathToReadinessTreeList.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import GiveBadgeDialog from "@components/GiveBadgeDialog/GiveBadgeDialog.vue";
import EvalResponsibilityModal from "@components/EvalResponsibilityModal/EvalResponsibilityModal.vue";
import RetireResponsibilityCatelog from "@components/RetireResponsibilityCatelog/RetireResponsibilityCatelog.vue";
import EmployeePageHeader from "@components/common/EmployeePageHeader.vue";
import {
  extractCreatedProficiencyBadgeId,
  proficiencyCheckpointErrorMessage,
} from "@/utils/proficiencyBadgeHelpers";
import {
  getListActiveCheckpointsEval,
  getListActiveCheckpointsEvalV2,
  createProficiencyBadge,
  activateEmployee,
  reactivateEmployee,
  unArchiveEmployee,
  deleteResponsibilityBadge,
  updateRetireResponsibility,
  getRoleAssignment,
  getListofResponsibility,
  getEmployeeAssignments,
  addPositionAssignment,
  getResponsibilityAssignment,
  getDirectResponsibilityAssignment,
  getAssignmentEval,
  getEmployeeBadges,
  getListOfPositionsForAssignment,
  getPositionId,
  getChatterList,
  recalculateEmployeeReadiness,
  getResponsibilityChecklistV2,
  passCheckPointEvaluation,
  failCheckPointEvaluation,
  notApplicableCheckPointEvaluation,
  resetCheckPointEvaluation,
} from "@/api";
import AssignEmployeeToPrimaryPositionDialog from "@components/AssignEmployeeToPrimaryPositionDialog/AssignEmployeeToPrimaryPositionDialog.vue";
import ActivateEmployee from "@components/modals/ActiveEmployee.vue";
import ReActivateEmployee from "@components/modals/ReActivateEmployee.vue";
import { usePositionStore } from "@/store/pinia/position";
import readinessRecalcMixin from "@/composables/readinessEmployees/readinessRecalcMixin";
import { useEmployeeStore } from "@/store/pinia/employee";
import BadgeConfirmationDialog from "@components/BadgeConfirmationDialog/BadgeConfirmationDialog.vue";
import ChecklistInspectionRightPane from "@components/ChecklistInspectionRightPane/ChecklistInspectionRightPane.vue";
import ReasonModal from "@components/ReasonModal/ReasonModal.vue";
import { useReadinessCatalogsStore } from "@/store/pinia/readinessCatalog";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import RemoveBadgeDialog from "@components/RemoveBadgeDialog/RemoveBadgeDialog.vue";
import UpgradeBadgeDialog from "@components/UpgradeBadgeDialog/UpgradeBadgeDialog.vue";
import {
  pickBadgeForResponsibility,
  mapBadgeRowToInspectionChip,
  isLevelCompleteForAward as isLevelCompleteForAwardCheck,
} from "@/utils/inspectionBadgeSync";
import { formatOrganizationEmployeeChatterNote } from "@/utils/chatterUtils";

export default {
  name: "EmployeePathToReadinessView",
  components: {
    PathToReadinessTreeList,
    GiveBadgeDialog,
    UpgradeBadgeDialog,
    ChecklistInspectionRightPane,
    EvalResponsibilityModal,
    AsCoachInspectWorkspace,
    NoRecordFound,
    SharedTableSkeleton,
    ActivateEmployee,
    ReActivateEmployee,
    AssignEmployeeToPrimaryPositionDialog,
    RetireResponsibilityCatelog,
    BadgeConfirmationDialog,
    AwardBadgeDialog,
    ExPagination,
    EmployeePageHeader,
    ChatterDrawer,
    RemoveBadgeDialog,
    ReasonModal,
  },
  props: {
    read_only: {
      type: Boolean,
      default: true,
    },
    /** When opened via drilldown, parent may pass employeeId before route has updated */
    employeeId: {
      type: [String, Number],
      default: null,
    },
    /**
     * Organization employee detail: only `employeeTab=pathtoreadiness` should set this true
     * so chatter shows "Issued" instead of API "Given". Responsibilities tab keeps raw notes.
     */
    formatChatterGivenAsIssued: {
      type: Boolean,
      default: false,
    },
    /** When embedded under EmployeeDetail (which already renders EmployeePageHeader). */
    hideHeader: {
      type: Boolean,
      default: false,
    },
    /** MDP-651 — Employee > Responsibilities tab: no Inspect action (Rule 15). Honoured only with the inline-inspect flag on. */
    hideInspect: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const vm = getCurrentInstance()?.proxy;
    const coachInspect = useCoachInspect({
      onViewClosed: () => vm?.onInlineInspectClosed?.(),
    });
    const inspectCtx = reactive({ ...coachInspect });
    // Vue does not merge `setup` across mixins; this one would replace the mixin's.
    return { ...readinessRecalcMixin.setup(), inspectCtx };
  },
  data() {
    return {
      dataSource: [],
      topLevel: "position",
      removeBorder: false,
      currentReadiness: null,
      currentSelectRes: null,
      currentResponsibility: null,
      isForceBadge: false,
      showGiveBadgeDialogFromInspection: false,
      giveBadgeDialogLockedLevel: null,
      showUpgradeBadgeDialog: false,
      inspectionGiveBadgeDefaultLevel: "apprentice",
      showEmployeeIssueBadge: false,
      isInspectResModal: false,
      inspectionCurrentCheckpoint: 0,
      allResponsibilities: [],
      checkpointsData: [],
      evalModalEmpState: null,
      evalModalHeaderState: null,
      activateEmployee: false,
      reActivateEmployee: false,
      unArchiveEmployee: false,
      assignPosition: false,
      currenntEmployee: null,
      errorMessage: "",
      positionStore: usePositionStore(),
      employeeStore: useEmployeeStore(),
      responsibilitiesToUseIds: [],
      showAwardBadgeModal: false,
      awardBadgeLevel: null,
      /** Triggers in-pane AwardBadgeDialog while inspection is open (token changes each time). */
      inspectionAwardDialogRequest: null,
      pendingAwardLevel: null,
      treeListKey: 0,
      badges: [
        {
          badge_id: "Apprentice",
          type: "Given",
        },
        {
          badge_id: "Professional",
          is_force: true,
        },
        {
          badge_id: "Coach",
        },
        {
          badge_id: "Master",
          is_force: false,
        },
      ],
      employeeData: null,
      employeeDataWithBadges: null,
      loading: {
        initial: false,
        readinessBar: false,
        responsibilities: {},
        assignments: false,
        employeeAssignment: false,
        forceBadge: false,
        upgradeBadge: false,
        removeBadge: false,
        employeeStatus: false,
        checkpoint: false,
      },
      cachedAccountID: null,
      cachedEmpId: null,
      cachedAuthUser: null,
      showRetireResponsibilityDialog: false,
      showBadgeConfirmationDialog: false,
      badgeData: null,
      dataLoading: false,
      skeletonLoadingEnabled: false,
      readinessStore: useReadinessCatalogsStore(),
      pagination: {
        limit: 10,
        offset: 0,
        total: 0,
        perPage: 10,
        page: 1,
      },
      // Add expanded row loading states
      expandedRowLoadingStates: {},
      currentCheckpointCounts: {},
      weeklyReadinessData: [],
      primaryPositions: {
        availablePositions: [],
        isLoadingMore: false,
        currentPage: 1,
        totalCount: 0,
        hasMoreData: true,
        currentSearchTerm: "",
        noResults: false,
        employeeVacancy: null,
        serverOffset: 0,
      },
      equalVacancyPicker: false,
      inlineInspectEnabled: false,
      assignRejectionMessage: "",
      assignEffectiveFrom: null,
      assignEffectiveTo: null,
      // Store expanded tree state
      expandedKeys: [],
      // ER-11627: one-shot guard so the default "expand one level below the
      // Position" seed only ever runs once per component instance — a later
      // initializeData() (force refresh, pagination) must never re-expand a
      // row the user deliberately collapsed.
      hasSeededDefaultExpansion: false,
      // Store loaded child data to prevent duplicate API calls during expansion
      loadedChildData: new Map(),
      // Flag to track if initial data load has completed
      isInitialLoadComplete: false,
      // Flag to prevent concurrent API calls
      isLoadingData: false,
      // Prevent re-entrant status-change handling (parent↔child emit loop / ER-9611)
      isProcessingStatusChange: false,
      // ER-11396: true while readiness is being recalculated after an importance
      // change. Drives the visible "processing" banner so the user never stares at
      // the generic full-page loader for minutes (which looks like an infinite
      // loading state) while the backend finishes the async recalculation.
      isRecalculating: false,
      showChatterDrawer: false,
      selectedBadgeChatter: null,
      chatterData: [],
      chatterPage: 1,
      totalChatter: 0,
      chatterLoading: false,
      chatterType: null, // 'badge' or 'responsibility'
      chatterTypeValue: null, // Badge level or responsibility name
      chatterIconName: null,
      showRemoveBadgeDialog: false,
      removeBadgeDialogFromInspection: false,
      selectedBadgeForRemoval: null,
      responsibilityCheckpoints: [],
      showReasonModal: false,
      reasonModalType: "fail",
      pendingReasonItem: null,
      reasonText: "",
    };
  },
  mixins: [dropdownMixin, readinessRecalcMixin],
  async mounted() {
    this.cachedAccountID = JSON.parse(localStorage.getItem("currentAccountID"));
    this.cachedAuthUser = JSON.parse(localStorage.getItem("user"));

    // Get employeeId: prop first (drilldown before route update), then route
    const routeEmpId = this.$route?.params?.id || this.$route?.query?.employeeId;
    const propEmpId = this.employeeId == null ? null : String(this.employeeId);
    const empId = propEmpId || routeEmpId;

    if (empId && empId !== "undefined" && empId !== "null") {
      this.cachedEmpId = empId;
    } else {
      // Route may not be updated yet when opened from employee list (drilldown)
      await this.$nextTick();
      const updatedRoute = this.$route?.params?.id || this.$route?.query?.employeeId;
      const updatedProp = this.employeeId == null ? null : String(this.employeeId);
      const fallback = updatedProp || updatedRoute;
      if (fallback && fallback !== "undefined" && fallback !== "null") {
        this.cachedEmpId = fallback;
      } else {
        console.error("No valid employeeId found in route on mount");
        this.$router.replace({ name: "NotFound" });
        return;
      }
    }

    this.$loader.show();
    this.dataLoading = true;
    this.$loader.hide();
    this.checkSkeletonFlag();
    this.checkEqualVacancyFlag();
    this.checkInlineInspectFlag();
    const success = await this.initializeData();
    if (!success) {
      this.$router.replace({ name: "NotFound" });
      return;
    }
    this.dataLoading = false;
    this.isInitialLoadComplete = true;
    await this.positionStore.loadPositions();
  },
  watch: {
    currentEmployeeStatus: {
      handler(newStatus, oldStatus) {
        this.handleStatusChange(newStatus, oldStatus);
      },
      immediate: false,
    },
    // Watch employeeInfo (Vuex) so content below updates when header/sidebar updates store
    employeeInfo: {
      handler(newEmployee, oldEmployee) {
        const empId = this.cachedEmpId || this.$route?.params?.id || this.$route?.query?.employeeId;
        if (!newEmployee || newEmployee.id != empId) return;

        const newStatus = (newEmployee.current_status || newEmployee.status || "").toLowerCase();
        const oldStatus = oldEmployee
          ? (oldEmployee.current_status || oldEmployee.status || "").toLowerCase()
          : "";

        // Sync store → employeeData so statusConfig and content below update
        const merged = this.employeeData
          ? { ...this.employeeData, ...newEmployee }
          : { ...newEmployee };
        if (oldStatus === "draft" && newStatus !== "terminated") {
          merged.status = "active";
          merged.current_status = "active";
        }
        this.$set(this, "employeeData", merged);
        this.$nextTick(() => this.$forceUpdate());

        this.handleStatusChange(newStatus, oldStatus, newEmployee);
      },
      deep: true,
      immediate: false,
    },
    // Watch employeeData status directly for immediate updates
    "employeeData.current_status": {
      handler(newStatus, oldStatus) {
        this.handleStatusChange(newStatus, oldStatus, this.employeeData);
      },
      immediate: false,
    },
    "employeeData.status": {
      handler(newStatus, oldStatus) {
        this.handleStatusChange(newStatus, oldStatus, this.employeeData);
      },
      immediate: false,
    },
    "$route.params.id": {
      handler(newId, oldId) {
        // Skip if it's the initial load (mounted() handles that) or if IDs are the same
        if (!oldId || newId === oldId) {
          return;
        }

        if (newId && newId !== oldId) {
          // Force refresh of employee data when route changes
          this.$nextTick(async () => {
            const success = await this.handleRefreshEmployeeData();
            if (!success) {
              this.$router.replace({ name: "NotFound" });
            }
          });
        }
      },
      immediate: false,
    },
    "$route.query.employeeId": {
      handler(newId, oldId) {
        // Handle query param changes
        if (
          newId &&
          newId !== oldId &&
          newId !== "undefined" &&
          newId !== "null" &&
          !this.$route.params.id
        ) {
          // Update cached employeeId
          this.cachedEmpId = String(newId);
          this.$nextTick(async () => {
            const success = await this.handleRefreshEmployeeData();
            if (!success) {
              this.$router.replace({ name: "NotFound" });
            }
          });
        }
      },
      immediate: false,
    },
    // Watch Pinia store so content below updates when header/sidebar updates store
    piniaCurrentEmployee: {
      handler(newEmployee) {
        const empId = this.cachedEmpId || this.$route?.params?.id || this.$route?.query?.employeeId;
        if (!newEmployee || newEmployee.id != empId) return;

        const oldStatus = (
          this.employeeData?.current_status ||
          this.employeeData?.status ||
          ""
        ).toLowerCase();

        const mergedData = this.employeeData
          ? { ...this.employeeData, ...newEmployee }
          : { ...newEmployee };
        if (oldStatus === "draft" && mergedData.current_status !== "terminated") {
          mergedData.status = "active";
          mergedData.current_status = "active";
        }
        this.$set(this, "employeeData", mergedData);
        this.$nextTick(() => this.$forceUpdate());

        const newStatus = (newEmployee.current_status || newEmployee.status || "").toLowerCase();
        if (newStatus === "terminated" && oldStatus !== "terminated") {
          this.clearPathToReadinessData();
        }
      },
      deep: true,
      immediate: false,
    },
  },

  computed: {
    inlineInspectOpen() {
      return this.inlineInspectEnabled && !!this.inspectCtx?.showInspectChecklistView;
    },
    hideInspectActions() {
      return this.inlineInspectEnabled && this.hideInspect;
    },
    inlineInspectBreadcrumbs() {
      return [
        { text: "Path to Readiness", href: "#", disabled: false },
        { text: "Inspection", disabled: true },
      ];
    },
    /** ER-10612 / MDP-560 — flag-gated table skeleton shown while the initial fetch is in flight. */
    showSkeletonLoading() {
      return this.dataLoading && this.skeletonLoadingEnabled;
    },
    awardBadgeDialogEmployee() {
      const e = this.evalModalEmpState;
      if (!e) return { name: "", avatar: "" };
      const name = [e.firstName, e.lastName].filter(Boolean).join(" ").trim() || e.firstName || "";
      return {
        name: name || "",
        avatar: e.photoUrl || "",
      };
    },
    awardBadgeResponsibilityName() {
      return (
        (this.currentResponsibility && this.currentResponsibility.name) ||
        (this.currentSelectRes &&
          (this.currentSelectRes.name || this.currentSelectRes.data?.data?.name)) ||
        ""
      );
    },
    awardBadgeDialogCurrentEmployee() {
      return this.buildEvalModalCurrentEmployeeForAwardDialog();
    },
    eligiblePositions() {
      if (!this.employeeData) return [];

      let employees = [];
      try {
        const exlAppData = JSON.parse(localStorage.getItem("exlAppData"));
        employees = exlAppData?.employees || [];
      } catch (error) {
        console.error("Error parsing exlAppData from localStorage:", error);
        employees = [];
      }

      if (!employees || !Array.isArray(employees)) {
        return [];
      }

      const employee = employees.find((el) => el.id === this.employeeData.id);
      const currentEmployeeData = employee || this.employeeData;
      const allPositions = this.positionStore.positions;
      let allocationSum = 0;

      if (currentEmployeeData.primary_positions?.length > 0) {
        currentEmployeeData.primary_positions.forEach((el) => {
          allocationSum += el.allocation;
        });
      }

      const employeeVacant = currentEmployeeData.availability - allocationSum;
      const allocationsArray = [];

      const processEmployeeAllocations = (emp) => {
        if (emp?.primary_positions?.length > 0) {
          emp.primary_positions.forEach((positionAssignment) => {
            const newEl = {
              id: positionAssignment.position,
              allocation: positionAssignment.allocation,
            };
            const itemExist = allocationsArray.some((el) => el.id === newEl.id);
            if (!itemExist) {
              allocationsArray.push(newEl);
            }
          });
        }
      };

      if (employees && Array.isArray(employees)) {
        employees.forEach(processEmployeeAllocations);
      }

      processEmployeeAllocations(currentEmployeeData);

      if (allPositions) {
        const positionsWithAllocation = allPositions.map((el) => {
          const allocationItem = allocationsArray.find((allocItem) => allocItem.id === el.id);
          return {
            ...el,
            allocation: allocationItem ? allocationItem.allocation : 0,
          };
        });

        return positionsWithAllocation.filter((el) => {
          const positionVacancy = (el?.capacity || 0) - (el?.allocation || 0);
          return (
            positionVacancy &&
            employeeVacant >= positionVacancy &&
            positionVacancy >= 1 && // does not show positions with 0.5 for now
            el.status == "active"
          );
        });
      }
      return [];
    },

    statusConfig() {
      // Read status from employeeData first for immediate updates, then fallback to store
      // This ensures real-time updates when employeeData changes
      const dataStatus = this.employeeData?.current_status || this.employeeData?.status;
      const storeStatus = this.$store.getters["employeeStore/currentEmployeeStatus"];
      const status = dataStatus || storeStatus;

      const isArchived =
        this.employeeData?.archived ||
        this.$store.getters["employeeStore/isCurrentEmployeeArchived"];

      const statuses = {
        draft: {
          title: "Employee Not Yet Activated",
          description:
            "This employee is currently in draft status. Activate them to unlock the Path to readiness and start tracking their progress.",
          btnTitle: "Activate",
        },
        active: {
          title: "No Position Assigned Yet",
          description: "To track this employee's readiness, assign them to a position first.",
          btnTitle: "Assign",
        },
        terminated_unarchived: {
          title: "Employee Status: Terminated",
          description:
            "This employee's Path to readiness is unavailable because they have been terminated. Re-activate them to resume tracking their progress.",
          btnTitle: "Re-Activate",
        },
        terminated_archived: {
          title: "Employee Status: Archived",
          description:
            "This employee's Path to readiness is unavailable because they have been archived. To resume tracking their progress, unarchive and re-activate them.",
          btnTitle: "Re-Activate",
        },
      };

      if (status === "terminated") {
        return isArchived ? statuses.terminated_archived : statuses.terminated_unarchived;
      }

      return (
        statuses[status] || {
          title: "",
          description: "",
          btnTitle: "",
        }
      );
    },

    title() {
      return this.statusConfig.title;
    },

    description() {
      return this.statusConfig.description;
    },

    btnTitle() {
      return this.statusConfig.btnTitle;
    },
    employeeInfo() {
      return this.$store.getters["employeeStore/currentEmployee"];
    },
    // Computed property to watch Pinia store for reactivity
    piniaCurrentEmployee() {
      return this.employeeStore.currentEmployee;
    },
    isNewLayoutEnabled() {
      return this.$store.getters["featureFlags/isNewDesign"];
    },
    employeeStatus() {
      // Force reactivity by reading from both store and local data
      const storeStatus = this.$store.getters["employeeStore/currentEmployeeStatus"];
      const dataStatus = this.employeeData?.current_status || this.employeeData?.status;
      return storeStatus || dataStatus || "draft";
    },
    currentEmployeeStatus() {
      // Force immediate status update by reading directly from employeeData first, then store
      // This ensures the status updates immediately when employeeData changes
      const dataStatus = this.employeeData?.current_status || this.employeeData?.status;
      const storeStatus = this.$store.getters["employeeStore/currentEmployeeStatus"];
      // Prioritize dataStatus to ensure immediate update
      return dataStatus || storeStatus || "draft";
    },
    employeeFullName() {
      if (this.employeeData?.first_name && this.employeeData?.last_name) {
        return `${this.employeeData.first_name} ${this.employeeData.last_name}`;
      }
      return this.employeeData?.name || "Employee";
    },
    inspectionCriteriaMessage() {
      const idx = this.inspectionCurrentCheckpoint ?? 0;
      const cp = this.checkpointsData?.[idx];
      return cp?.criteria ?? cp?.instructions ?? "";
    },
    inspectionPaneEmployee() {
      const emp = this.employeeData;
      // While inspecting, trust evalModalEmpState (updated by sync after API); tree node badge can lag after reload.
      const badge = this.isInspectResModal
        ? this.evalModalEmpState?.currentEmployeeBadge ?? this.currentSelectRes?.data?.data?.badge
        : this.currentSelectRes?.data?.data?.badge ?? this.evalModalEmpState?.currentEmployeeBadge;
      const badgeLevel = badge?.level;
      let levelStr;
      if (badgeLevel == null) {
        levelStr = "Novice";
      } else if (typeof badgeLevel === "string") {
        levelStr = badgeLevel;
      } else {
        levelStr = this.getBadgeLevelStringForDialog(badgeLevel) || "Novice";
      }
      return {
        id: emp?.id,
        fullName: emp
          ? [emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.name || "Employee"
          : "Employee",
        photoUrl: emp?.avatar ?? emp?.photoUrl ?? "",
        position:
          emp?.primary_position?.name ??
          emp?.title ??
          emp?.position?.name ??
          this.evalModalHeaderState?.positionName ??
          "",
        badge: {
          level: levelStr,
          dot: badge?.dot ?? "Good Standing",
          id: badge?.id,
        },
      };
    },
    /**
     * Plain snapshot for the inspection ReadinessBadge (Vue 2 → Vue 3 boundary).
     * Recomputed when evalModalEmpState / tree badge changes so the chip always receives new props.
     */
    inspectionReadinessChip() {
      if (!this.isInspectResModal) return null;
      const currentBadge = this.evalModalEmpState?.currentEmployeeBadge;
      const selectedBadge = this.currentSelectRes?.data?.data?.badge;
      const b = this.inspectionPaneEmployee?.badge ?? currentBadge ?? selectedBadge;
      return {
        level: b?.level ?? "Novice",
        dot: b?.dot ?? "Good Standing",
        id: b?.id ?? null,
      };
    },
    inspectionResponsibilityAssignment() {
      const h = this.evalModalHeaderState;
      const r = this.currentResponsibility;
      if (!h && !r) {
        return {
          responsibilityCode: r?.code ?? "#—",
          responsibilityName: r?.name ?? "",
          responsibilityImportance: 2,
          roleCode: "",
          roleName: "",
          roleImportance: 2,
          positionCode: "",
          positionName: "",
          positionImportance: 2,
          positionCommitment: "Primary",
          assignedOn: null,
          newDuration: null,
          show_codes: true,
        };
      }
      return {
        responsibilityId: r?.id,
        responsibilityCode: h?.responsibilityCode ?? r?.code ?? "#—",
        responsibilityName: h?.responsibilityName ?? r?.name ?? "",
        responsibilityImportance: 2,
        roleCode: h?.roleCode ?? "",
        roleName: h?.roleName ?? "",
        roleImportance: 2,
        positionCode: h?.positionCode ?? "",
        positionName: h?.positionName ?? "",
        positionImportance: 2,
        positionCommitment: "Primary",
        assignedOn: null,
        newDuration: null,
        show_codes: true,
      };
    },
    isArchived() {
      return (
        this.$store.getters["employeeStore/isCurrentEmployeeArchived"] ||
        this.employeeData?.archived ||
        false
      );
    },
    isTracking() {
      // Check if employee has tracking enabled (has positions assigned)
      return (this.employeeData?.primary_positions?.length ?? 0) > 0;
    },
    lastMonthReadiness() {
      // Get from weeklyReadinessData if available, or from employeeData
      if (this.weeklyReadinessData && this.weeklyReadinessData.length > 0) {
        // Return the second to last week's readiness (last month)
        return this.weeklyReadinessData[this.weeklyReadinessData.length - 2] || null;
      }
      return this.employeeData?.last_month_readiness || null;
    },
    breadcrumbsList() {
      return [
        {
          text: "Employees",
          disabled: false,
          href: "/organization/employees",
        },
        {
          text: this.employeeFullName,
          disabled: true,
          truncate: true,
        },
      ];
    },
    giveBadgeDialogEmployeeName() {
      if (this.showGiveBadgeDialogFromInspection && this.evalModalEmpState) {
        const e = this.evalModalEmpState;
        if (e.firstName && e.lastName) {
          return `${e.firstName} ${e.lastName}`;
        }
        return e.name || "";
      }
      const emp = this.employeeData || this.employeeDataWithBadges || this.employeeInfo;
      if (emp?.first_name && emp?.last_name) {
        return `${emp.first_name} ${emp.last_name}`;
      }
      return emp?.name || "";
    },
    giveBadgeDialogCanNotify() {
      const emp = this.showGiveBadgeDialogFromInspection
        ? this.evalModalEmpState
        : this.employeeDataWithBadges ||
          this.employeeData ||
          this.employeeInfo ||
          this.evalModalEmpState;
      const access = emp?.access;
      if (access == null) return false;
      if (access.is_suspended === true) return false;
      if (access.has_access === false) return false;
      return true;
    },
    inspectionAwardBadgeDialogCurrentEmployee() {
      return this.buildEvalModalCurrentEmployeeForAwardDialog() ?? {};
    },
    giveBadgeDialogCurrentLevel() {
      if (this.showGiveBadgeDialogFromInspection && this.evalModalEmpState?.currentEmployeeBadge) {
        const level = this.evalModalEmpState.currentEmployeeBadge.level;
        const str = typeof level === "string" ? level : this.getBadgeLevelStringForDialog(level);
        return str ? str.toLowerCase() : null;
      }
      const employee = this.employeeDataWithBadges || this.employeeData || this.employeeInfo;
      const responsibility = this.currentResponsibility;
      if (!employee || !responsibility) return null;
      const badgeArray = employee.badges || employee.badgeList || [];
      const targetCode = responsibility.code_str?.startsWith("#")
        ? responsibility.code_str.substring(1)
        : this.extractCode(responsibility.code_str) || responsibility.code;
      const foundBadge = badgeArray.find((b) => {
        const issuedCode = this.extractCode(b?.issued_for?.code);
        const isCurrent = b.is_current == 1;
        return issuedCode === targetCode && isCurrent;
      });
      if (!foundBadge) return null;
      const level =
        typeof foundBadge.level === "string"
          ? foundBadge.level
          : this.getBadgeLevelStringForDialog(foundBadge.level);
      return level ? level.toLowerCase() : null;
    },
    giveBadgeDialogDescription() {
      const respName = this.currentResponsibility?.name || "this responsibility";
      const empName = this.giveBadgeDialogEmployeeName || "the employee";
      if (this.showGiveBadgeDialogFromInspection) {
        return `Responsibility ${respName} has no Readiness Checklist therefore you can only Give (Instead of Awarding) a Badge to employee ${empName}.`;
      }
      return `Responsibility ${respName} has no Readiness Checklist therefore you can only Give (Instead of Awarding) a Badge to employee ${empName}.`;
    },
    giveBadgeDialogResponsibilityName() {
      return this.currentResponsibility?.name || "";
    },
    giveBadgeDialogDefaultLevel() {
      return this.showGiveBadgeDialogFromInspection
        ? this.inspectionGiveBadgeDefaultLevel
        : "apprentice";
    },
    /** Current badge level for UpgradeBadgeDialog (only used when showing upgrade; must be non-Master). */
    upgradeBadgeDialogCurrentLevel() {
      const badge =
        this.evalModalEmpState?.currentEmployeeBadge || this.inspectionPaneEmployee?.badge;
      let level = null;
      if (badge?.level != null) {
        level =
          typeof badge.level === "string"
            ? badge.level
            : this.getBadgeLevelStringForDialog(badge.level);
      }
      const normalized = level ? level.toLowerCase() : null;
      if (normalized && ["apprentice", "professional", "coach", "master"].includes(normalized)) {
        return normalized;
      }
      return "apprentice";
    },
  },
  methods: {
    /** ER-10612 / MDP-560 — resolve the consistent-skeleton feature flag; off/failing -> legacy blank gap. */
    async checkSkeletonFlag() {
      try {
        this.skeletonLoadingEnabled = (await this.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
      } catch (error) {
        this.skeletonLoadingEnabled = false;
      }
    },
    /** MDP-647 — equal-vacancy primary picker; off/failing -> legacy picker. */
    async checkEqualVacancyFlag() {
      try {
        this.equalVacancyPicker = (await this.$getFlag?.(LD_FLAG_IDEA_MDP_647, false)) === true;
      } catch (error) {
        this.equalVacancyPicker = false;
      }
    },
    /** MDP-651 — inline As Coach checklist for Inspect; off/failing -> legacy Organization inspection route. */
    async checkInlineInspectFlag() {
      try {
        this.inlineInspectEnabled = (await this.$getFlag?.(LD_FLAG_IDEA_MDP_651, false)) === true;
      } catch (error) {
        this.inlineInspectEnabled = false;
      }
    },
    handleStatusChange(newStatus, oldStatus, employeeData = null) {
      const normalizedNew = (newStatus || "").toLowerCase();
      const normalizedOld = (oldStatus || "").toLowerCase();

      if (normalizedNew === "terminated" && normalizedOld !== "terminated") {
        const dataToUpdate =
          employeeData ||
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];

        if (dataToUpdate) {
          this.updateEmployeeDataForTermination(dataToUpdate);
        }
      }
    },
    async openChatterDrawer(data) {
      // Determine the type based on the data structure
      // data comes from PathToReadinessTreeList as options.data
      const itemData = data?.data || data;

      // Check if it's a badge (has badge property)
      if (itemData?.badge && itemData.badge.id) {
        // It is a badge chatter
        this.selectedBadgeChatter = itemData.badge;
        this.chatterType = "badge";
        this.chatterTypeValue = this.getBadgeLevelName(itemData.badge.level);
        this.chatterIconName = "org_icon_responsibility";
      } else if (itemData?.id) {
        // Responsibility chatter (or fallback when assignmentType is missing)
        this.selectedBadgeChatter = itemData;
        this.chatterType = "responsibility";
        this.chatterTypeValue = itemData.name || "";
        this.chatterIconName = "org_icon_responsibility";
      } else {
        console.warn("Unable to determine chatter type for data:", data);
        return;
      }

      // Reset chatter data when opening drawer
      this.chatterData = [];
      this.chatterPage = 1;
      this.totalChatter = 0;
      this.showChatterDrawer = true;

      // Fetch chatter data
      await this.fetchChatter();
    },
    closeChatterDrawer() {
      this.showChatterDrawer = false;
      this.selectedBadgeChatter = null;
      this.chatterData = [];
      this.chatterPage = 1;
      this.totalChatter = 0;
      this.chatterType = null;
      this.chatterTypeValue = null;
      this.chatterIconName = null;
    },
    async fetchChatter() {
      if (
        this.chatterLoading ||
        !this.selectedBadgeChatter ||
        !this.selectedBadgeChatter.id ||
        !this.chatterType
      )
        return;

      this.chatterLoading = true;
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        const recordId = this.selectedBadgeChatter.id;

        const { data } = await getChatterList(
          accountID,
          recordId,
          this.chatterType,
          this.chatterPage
        );

        if (data && data.count) {
          this.totalChatter = data.count;
        }

        if (data && data.results) {
          const chatterItems = data.results.map((el) => {
            const date = new Date(el.recorded_on);
            const milliseconds = date.getTime();
            const millisecondsString = milliseconds.toString();
            const nameParts = el.recorded_by_name ? el.recorded_by_name.split(" ") : [""];

            const noteText = this.formatChatterGivenAsIssued
              ? formatOrganizationEmployeeChatterNote(el.note)
              : el.note;
            const newEl = {
              id: el.id,
              IsExIQtiveBot: false,
              message: noteText,
              created_on: millisecondsString,
              IsHistory: el.is_history,
            };

            if (nameParts.length >= 2) {
              newEl.avatar = {
                picture: "",
                firstName: nameParts[0],
                lastName: nameParts.slice(1).join(" "),
              };
            } else {
              newEl.avatar = {
                picture: "",
                firstName: el.recorded_by_name || "",
                lastName: "",
              };
            }

            return newEl;
          });

          this.chatterData.push(...chatterItems);
        }
      } catch (error) {
        console.error("Error fetching chatter:", error);
      } finally {
        this.chatterLoading = false;
      }
    },
    async loadMoreChatter() {
      if (this.chatterLoading) return;
      this.chatterPage += 1;
      await this.fetchChatter();
    },
    getBadgeLevelName(level) {
      if (typeof level === "string") {
        return level.charAt(0).toUpperCase() + level.slice(1);
      }
      const levelMap = {
        1: "Apprentice",
        2: "Professional",
        3: "Coach",
        4: "Master",
      };
      return levelMap[level] || "Apprentice";
    },

    async handleEmployeeUpdate(payload) {
      if (payload && payload.id) {
        this.$set(this, "employeeData", this.normalizeEmployeeData(payload));
      } else {
        await this.getEmployeeData();
      }
      if (this.isTerminatedEmployee(this.employeeData)) {
        this.clearPathToReadinessData();
        return;
      }
      await this.reloadDataAfterChanges();
    },
    clearPathToReadinessData() {
      this.dataSource = [];
      this.pagination.total = 0;
      this.treeListKey++;
      this.loadedChildData.clear();
      this.expandedKeys = [];
      this.weeklyReadinessData = [];
      this.$forceUpdate();
    },
    normalizeEmployeeData(employeeData) {
      return {
        ...employeeData,
        status: employeeData.status || employeeData.current_status,
        current_status: employeeData.current_status || employeeData.status,
      };
    },
    findPositionIndex(positionAssignmentId) {
      return this.dataSource.findIndex((item) => item.id === `position_${positionAssignmentId}`);
    },
    async updateEmployeeDataForTermination(employeeData) {
      const normalized = this.normalizeEmployeeData(employeeData);
      // Only update local UI — do not write stores again (caller/store already did).
      // Rewriting stores re-triggers watchers and caused GET storms (ER-9611).
      this.$set(this, "employeeData", { ...normalized });

      await this.$nextTick();
      this.$forceUpdate();

      this.clearPathToReadinessData();
    },
    async handleEmployeeStatusChanged(payload) {
      if (this.isProcessingStatusChange) return;
      this.isProcessingStatusChange = true;

      const empId = this.cachedEmpId || this.$route?.params?.id || this.$route?.query?.employeeId;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));

      try {
        // Prefer payload from sidebar terminate/reactivate — avoid a second GET (ER-9611)
        let freshEmployeeData = payload && payload.id ? payload : null;

        if (!freshEmployeeData && this.isTerminatedEmployee(this.employeeData)) {
          this.clearPathToReadinessData();
          return;
        }

        if (!freshEmployeeData) {
          if (!empId || !accountID) {
            this.$emit("employeeStatusChanged");
            return;
          }
          freshEmployeeData = await this.employeeStore.fetchCurrentEmployee(empId, true);
        }

        if (!freshEmployeeData) {
          this.$emit("employeeStatusChanged");
          return;
        }

        const normalizedEmployeeData = this.normalizeEmployeeData(freshEmployeeData);

        this.$store.commit("employeeStore/setCurrentEmployee", normalizedEmployeeData);
        this.employeeStore.setCurrentEmployee(normalizedEmployeeData);

        this.$set(this, "employeeData", { ...normalizedEmployeeData });

        await this.$nextTick();
        this.$forceUpdate();

        if (this.isTerminatedEmployee(normalizedEmployeeData)) {
          this.clearPathToReadinessData();
          // Do not emit employeeStatusChanged — parent already owns refresh via store
          // watchers / its own handler. Re-emitting caused an infinite GET loop (ER-9611).
          return;
        }

        await this.handleRefreshEmployeeData();
        this.$emit("employeeStatusChanged", normalizedEmployeeData);
      } catch (error) {
        console.error("Error in employee status refresh:", error);
        const fallbackEmployeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];

        if (fallbackEmployeeData) {
          const normalized = this.normalizeEmployeeData(fallbackEmployeeData);
          this.$set(this, "employeeData", { ...normalized });
          this.$forceUpdate();

          if (this.isTerminatedEmployee(normalized)) {
            this.clearPathToReadinessData();
            return;
          }
        }
        this.$emit("employeeStatusChanged");
      } finally {
        this.isProcessingStatusChange = false;
      }
    },
    async handleEmployeeDeleted() {
      // Redirect to employees list when employee is deleted
      this.$router.push({ name: "Employees" });
    },
    async handleRefreshEmployeeData() {
      // Force a complete refresh of employee data from API
      return await this.initializeData(true);
    },
    getEmpAndAccountIds() {
      // Always get the latest employeeId from route, don't rely on cached value
      const empId = this.$route?.params?.id || this.$route?.query?.employeeId || this.cachedEmpId;

      // Update cached value if we got a new one from route
      if (empId && empId !== this.cachedEmpId) {
        this.cachedEmpId = empId;
      }

      // Validate that we have a valid employeeId
      if (!empId || empId === "undefined" || empId === "null") {
        console.error("Invalid employeeId:", empId);
        return {
          empId: null,
          accountID: this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID")),
        };
      }

      return {
        empId: String(empId), // Ensure it's a string
        accountID: this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID")),
      };
    },
    shouldFetchEmployee(force, employeeData, empId) {
      return (
        force || !employeeData || Object.keys(employeeData).length === 0 || employeeData.id != empId
      );
    },
    async fetchAndSetEmployeeData(empId) {
      try {
        const employeeData = await this.employeeStore.fetchCurrentEmployee(empId, true);
        if (employeeData) {
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeStore.setCurrentEmployee(employeeData);
        }
        return employeeData;
      } catch (error) {
        console.error("Error fetching employee data:", error);
        if (error.response && error.response.status === 404) {
          return null;
        }
        throw error;
      }
    },
    applyEmployeeToState(employeeData) {
      this.employeeData = employeeData;
      this.currentReadiness = {
        current_readiness: employeeData.current_readiness,
      };
    },
    isTerminatedEmployee(employeeData) {
      const status = employeeData?.current_status || employeeData?.status || "";
      return status.toLowerCase() === "terminated";
    },
    async loadAssignmentsAndWeeklyData(accountID, empId) {
      const [assignmentsResponse, weeklyDataResponse] = await Promise.all([
        getEmployeeAssignments(
          accountID,
          empId,
          this.pagination.limit,
          this.pagination.offset,
          "active"
        ),
        this.fetchWeeklyReadinessData(),
      ]);
      if (assignmentsResponse.data) {
        this.pagination.total = assignmentsResponse.data.count || 0;
        this.dataSource =
          assignmentsResponse.data.results?.length > 0
            ? this.transformData(assignmentsResponse.data.results)
            : [];
      }
      if (weeklyDataResponse) {
        this.weeklyReadinessData = weeklyDataResponse;
      }
    },
    resetTreeListState() {
      this.treeListKey++;
      this.expandedKeys = [];
      this.loadedChildData.clear();
    },
    async initializeData(force = false) {
      if (this.isLoadingData) return true;

      this.isLoadingData = true;
      this.$loader.show();
      const { empId, accountID } = this.getEmpAndAccountIds();

      // Validate employeeId before proceeding
      if (!empId || empId === "undefined" || empId === "null") {
        console.error("Cannot initialize data: Invalid employeeId:", empId);
        this.$loader.hide();
        this.isLoadingData = false;
        return false;
      }

      try {
        this.loading.readinessBar = true;

        let employeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];
        if (this.shouldFetchEmployee(force, employeeData, empId)) {
          employeeData = await this.fetchAndSetEmployeeData(empId);
        }

        if (!employeeData || !employeeData.id) {
          console.error("Employee data not found for ID:", empId);
          return false;
        }

        this.applyEmployeeToState(employeeData);

        // Check if employee is terminated - if so, don't fetch assignments
        if (this.isTerminatedEmployee(employeeData)) {
          // Clear dataSource for terminated employees
          this.dataSource = [];
          this.pagination.total = 0;
        } else {
          await this.loadAssignmentsAndWeeklyData(accountID, empId);
        }

        this.resetTreeListState();
        // ER-11627: expand the top-level Position row(s) one level by default.
        // Must run after resetTreeListState() — that call unconditionally
        // clears expandedKeys, so seeding earlier (e.g. right where dataSource
        // is built) would be wiped out before it ever reached the tree.
        this.seedDefaultExpansion();
        // ER-11627 rework: the reactive expandedKeys prop is normally enough
        // (PathToReadinessTreeList watches it and calls applyExpansionState()),
        // but the tree isn't mounted yet at this point — it sits behind
        // dataLoading's v-if and only appears once mounted() (the caller of
        // initializeData()) flips dataLoading back to false. Push the seeded
        // keys straight into the DevExtreme instance once it exists so the
        // very first paint is never left collapsed by a missed prop update.
        this.$nextTick(() => this.enforceSeededExpansion());
        return true;
      } catch (error) {
        console.error("Error initializing data:", error);
        return false;
      } finally {
        this.$loader.hide();
        this.loading.readinessBar = false;
        this.isLoadingData = false;
      }
    },
    // ER-11627: seed expandedKeys once so the Position row(s) render expanded
    // one level (directly attached roles/responsibilities visible, those rows
    // themselves collapsed). Guarded by hasSeededDefaultExpansion so a later
    // initializeData() call (force refresh, pagination) never re-expands a row
    // the user deliberately collapsed.
    seedDefaultExpansion() {
      if (this.hasSeededDefaultExpansion) {
        return;
      }
      this.hasSeededDefaultExpansion = true;

      // Root Position rows that actually have children (mirrors the
      // has_items logic PathToReadinessTreeList derives for these rows), so
      // no chevron-less row is ever pushed into expandedRowKeys.
      const rootRowsWithItems = this.dataSource.filter(
        (item) =>
          item.parentId === -1 && (item.data?.hasRoles || item.data?.hasDirectResponsibilities)
      );

      if (rootRowsWithItems.length === 0) {
        return;
      }

      // With more than one active position assignment, only the primary
      // position auto-expands. Fall back to expanding every root row when
      // none is marked Primary (e.g. a single non-primary position), so a
      // lone position assignment still expands regardless of its commitment
      // level.
      const primaryRows = rootRowsWithItems.filter(
        (item) => item.data?.commitmentLevel === "Primary"
      );
      const rowsToExpand = primaryRows.length > 0 ? primaryRows : rootRowsWithItems;

      this.expandedKeys = rowsToExpand.map((item) => item.id);

      // ER-11627 rework #2 (root cause): the two prior attempts both seeded
      // `expandedRowKeys` — reactively via the prop, then imperatively via
      // `enforceSeededExpansion()` below — and stopped there, on the
      // assumption that DevExtreme's own `onOptionChanged` would notice the
      // row is expanded and drive PathToReadinessTreeList's existing
      // treeExpandRole -> onTreeExpandRole lazy-load. It never does:
      // `expandedRowKeys` is being supplied as part of the tree's *initial*
      // construction (the whole view sits behind `dataLoading`'s v-if until
      // this data is already seeded), and DevExtreme only raises
      // `onOptionChanged` for a later collapsed->expanded *transition*
      // (e.g. a real user click), never for a value already present when the
      // widget is built. So the Position row rendered "expanded" over a
      // dataSource that still had no child rows in it — indistinguishable
      // from collapsed, which is exactly what kept reproducing.
      // refreshExpandedChildData() below already hits this same gap for a
      // force-reload and works around it by calling the position/role
      // loader directly instead of depending on that event; do the same
      // here for every seeded row rather than waiting on an event that
      // will not fire.
      rowsToExpand.forEach((item) => {
        this.onTreeExpandRole({
          assignmentId: item.assignment_id,
          rowType: "position",
          expandedRowData: item,
        });
      });
    },
    // ER-11627 rework: reported still-collapsed on load even after the
    // seeding above. The reactive `:expandedKeys` binding is normally
    // sufficient (see PathToReadinessTreeList's own watcher/mounted hook),
    // but this component is itself hidden behind `v-if="!dataLoading"` while
    // initializeData() runs, so the very first time the real DxTreeList
    // instance exists is a tick or more after this method is scheduled.
    // Reach directly into the DevExtreme instance — same pattern already
    // used by preserveExpansionState() below — and set expandedRowKeys once
    // it is mounted, retrying across a few ticks instead of dropping the
    // seed silently the way a single expandRow() no-op would.
    enforceSeededExpansion(attempt = 0) {
      if (!this.expandedKeys.length) {
        return;
      }
      const treeListRef =
        this.$refs &&
        this.$refs.PathToReadinessTreeList &&
        this.$refs.PathToReadinessTreeList.$refs &&
        this.$refs.PathToReadinessTreeList.$refs.treelist;
      const treeInstance = treeListRef && treeListRef.instance;

      if (!treeInstance) {
        if (attempt < 5) {
          this.$nextTick(() => this.enforceSeededExpansion(attempt + 1));
        }
        return;
      }

      try {
        treeInstance.option("expandedRowKeys", [...this.expandedKeys]);
      } catch (error) {
        console.error("Could not apply seeded expansion state:", error);
      }
    },
    async fetchWeeklyReadinessData() {
      try {
        const employeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];

        if (employeeData?.weekly_readiness_history) {
          return employeeData.weekly_readiness_history;
        }

        const currentReadiness = employeeData?.current_readiness || 0;
        const sampleWeeklyData = this.generateSampleWeeklyData(currentReadiness);

        return sampleWeeklyData;
      } catch (error) {
        console.error("Error fetching weekly readiness data:", error);
        return [];
      }
    },

    generateSampleWeeklyData(currentReadiness) {
      const weeks = [];
      const baseReadiness = Math.max(0, currentReadiness - 20);

      for (let i = 0; i < 7; i++) {
        // Create slight variations around the current readiness
        const variation = Math.random() * 10 - 5; // -5 to +5 variation
        const readiness = Math.min(100, Math.max(0, baseReadiness + i * 3 + variation));
        weeks.push(Math.round(readiness));
      }

      // Ensure the last week matches current readiness
      weeks[6] = currentReadiness;

      return weeks;
    },
    async fetchEqualVacancyPositions(searchTerm = "", isLoadMore = false) {
      const positions = this.primaryPositions;
      try {
        if (isLoadMore) {
          positions.isLoadingMore = true;
        }
        if (!isLoadMore) {
          positions.employeeVacancy = await fetchEmployeeVacancy({
            accountId: this.cachedAccountID,
            employee: this.employeeData,
            effectiveFrom: this.assignEffectiveFrom,
            effectiveTo: this.assignEffectiveTo,
          });
        }
        const result = await loadEqualVacancyPositions({
          accountId: this.cachedAccountID,
          name: searchTerm || "",
          offset: isLoadMore ? positions.serverOffset : 0,
          effectiveFrom: this.assignEffectiveFrom,
          effectiveTo: this.assignEffectiveTo,
          employeeVacancy: positions.employeeVacancy,
          excludeCodeStrs: (this.employeeData?.preparing_for_positions || []).map(
            (position) => position.code_str
          ),
        });
        positions.availablePositions = isLoadMore
          ? [...positions.availablePositions, ...result.items]
          : result.items;
        positions.serverOffset = result.nextOffset;
        positions.totalCount = result.totalCount;
        positions.hasMoreData = result.hasMore;
        positions.noResults = positions.availablePositions.length === 0 && !result.hasMore;
      } catch (error) {
        console.error("Error fetching available positions:", error);
      } finally {
        positions.isLoadingMore = false;
      }
    },
    async onAssignDateChange({ effectiveFrom, effectiveTo }) {
      this.assignEffectiveFrom = effectiveFrom;
      this.assignEffectiveTo = effectiveTo;
      this.assignRejectionMessage = "";
      await this.fetchEqualVacancyPositions(this.primaryPositions.currentSearchTerm, false);
    },
    async fetchAvailablePositions(page = 1, searchTerm = "", isLoadMore = false) {
      if (this.equalVacancyPicker) {
        await this.fetchEqualVacancyPositions(searchTerm, isLoadMore);
        return;
      }
      const limit = 100;
      const offset = (page - 1) * limit;
      const name = searchTerm || "";
      try {
        if (isLoadMore) {
          this.primaryPositions.isLoadingMore = true;
        }
        const response = await getListOfPositionsForAssignment({
          accountId: this.cachedAccountID,
          limit,
          offset,
          name,
        });

        if (!isLoadMore && searchTerm && response.data?.results?.length === 0) {
          this.primaryPositions.noResults = true;
          // Search returned nothing, re-fetch all data as fallback
          const allPositions = await getListOfPositionsForAssignment({
            accountId: this.cachedAccountID,
            limit,
            offset: 0,
            name: "",
          });

          let allItems =
            allPositions.data?.results?.map((item) => ({
              id: item.id,
              code: item.code_str.match(/\d+/) ? item.code_str.match(/\d+/)[0] : item.id,
              code_str: item.code_str,
              name: item.name,
              status: item.status,
              isInvalid: Boolean(item.is_invalid),
              directResponsibilitiesCount: item.direct_responsibilities_count || 0,
              notActiveDirectResponsibilitiesCount:
                item.not_active_direct_responsibilities_count || 0,
              validRolesCount: item.valid_roles_count || 0,
              invalidRolesCount: item.invalid_roles_count || 0,
              validResponsibilitiesCount: item.active_responsibilities_count || 0,
              capacity: item.capacity || 0,
              allocation:
                item.allocation === 0 || !item.allocation ? "not" : String(item.allocation),
              archived: item.archived || false,
              invalidityReason: item.invalidity_reason || "",
              disabled:
                !!(item.primary_occupied_by && item.primary_occupied_by.length > 0) ||
                !!(
                  item.occupied_by &&
                  (Array.isArray(item.occupied_by)
                    ? item.occupied_by.length > 0
                    : !!item.occupied_by)
                ) ||
                !!item.assigned_primary ||
                item.vacant === 0 ||
                item.vacancy === 0 ||
                (item.status && String(item.status).toLowerCase() !== "active"),
            })) || [];

          if (this.employeeData?.preparing_for_positions?.length > 0) {
            const preparingCodes = new Set(
              this.employeeData.preparing_for_positions.map((p) => p.code_str)
            );
            allItems = allItems.filter((item) => !preparingCodes.has(item.code_str));
          }

          this.primaryPositions.availablePositions = allItems;
          this.primaryPositions.currentPage = 1;
          this.primaryPositions.totalCount = allPositions.data?.count || 0;
          this.primaryPositions.hasMoreData = limit < this.primaryPositions.totalCount;
          this.primaryPositions.isLoadingMore = false;
          return;
        }

        this.primaryPositions.noResults = false;
        const positions = response.data?.results || [];
        let newItems = positions.map((position) => ({
          id: position.id,
          code: position.code_str.match(/\d+/) ? position.code_str.match(/\d+/)[0] : position.id,
          code_str: position.code_str,
          name: position.name,
          status: position.status,
          isInvalid: Boolean(position.is_invalid),
          directResponsibilitiesCount: position.direct_responsibilities_count || 0,
          notActiveDirectResponsibilitiesCount:
            position.not_active_direct_responsibilities_count || 0,
          validRolesCount: position.valid_roles_count || 0,
          invalidRolesCount: position.invalid_roles_count || 0,
          validResponsibilitiesCount: position.active_responsibilities_count || 0,
          capacity: position.capacity || 0,
          allocation:
            position.allocation === 0 || !position.allocation ? "not" : String(position.allocation),
          archived: position.archived || false,
          invalidityReason: position.invalidity_reason || "",
          disabled:
            !!(position.primary_occupied_by && position.primary_occupied_by.length > 0) ||
            !!(
              position.occupied_by &&
              (Array.isArray(position.occupied_by)
                ? position.occupied_by.length > 0
                : !!position.occupied_by)
            ) ||
            !!position.assigned_primary ||
            position.vacant === 0 ||
            position.vacancy === 0 ||
            (position.status && String(position.status).toLowerCase() !== "active"),
        }));

        // Filter out positions if current employee has preparing_for_positions matching them
        if (this.employeeData?.preparing_for_positions?.length > 0) {
          const preparingCodes = new Set(
            this.employeeData.preparing_for_positions.map((p) => p.code_str)
          );
          newItems = newItems.filter((item) => !preparingCodes.has(item.code_str));
        }
        if (isLoadMore && this.primaryPositions.availablePositions) {
          this.primaryPositions.availablePositions = [
            ...this.primaryPositions.availablePositions,
            ...newItems,
          ];
        } else {
          // If it's initial load or search, replace items
          this.primaryPositions.availablePositions = newItems;
        }
        // Update pagination state
        this.primaryPositions.currentPage = page;
        this.primaryPositions.totalCount = response.data?.count || 0;
        // hasMoreData should be true if there are more records beyond current page
        this.primaryPositions.hasMoreData = page * limit < this.primaryPositions.totalCount;
        this.primaryPositions.isLoadingMore = false;
      } catch (error) {
        this.primaryPositions.isLoadingMore = false;
        console.error("Error fetching available positions:", error);
      }
    },
    async loadMorePrimaryPositions() {
      if (this.primaryPositions.isLoadingMore || !this.primaryPositions.hasMoreData) {
        return;
      }

      const nextPage = this.primaryPositions.currentPage + 1;
      await this.fetchAvailablePositions(nextPage, this.primaryPositions.currentSearchTerm, true);
    },

    // Method to handle search with pagination reset
    async searchPrimaryPositions(searchTerm) {
      this.primaryPositions.currentSearchTerm = searchTerm;
      this.primaryPositions.currentPage = 1;
      this.primaryPositions.hasMoreData = true;
      await this.fetchAvailablePositions(
        this.primaryPositions.currentPage,
        this.primaryPositions.currentSearchTerm,
        false
      );
    },
    normalizeInspectionAwardLevel(obj) {
      const level = normalizeBadgeLevel(String(obj?.level ?? obj?.value ?? ""));
      if (level === "novice") return "apprentice";
      if (["apprentice", "proficient", "coach", "architect"].includes(level)) {
        return level;
      }
      return "apprentice";
    },
    showAwardBadge(obj) {
      // When employee has a current badge (not Master) and responsibility has no active checkpoints,
      // show Upgrade Badge modal with only levels > current level.
      const hasNoActiveCheckpoints =
        !this.checkpointsData?.length && !this.responsibilityCheckpoints?.length;
      const currentBadge =
        this.evalModalEmpState?.currentEmployeeBadge || this.inspectionPaneEmployee?.badge;
      let currentLevelRaw = null;
      if (currentBadge?.level != null) {
        if (typeof currentBadge.level === "string") {
          currentLevelRaw = currentBadge.level;
        } else {
          currentLevelRaw = this.getBadgeLevelStringForDialog(currentBadge.level);
        }
      }
      const currentLevel = currentLevelRaw ? currentLevelRaw.toLowerCase() : null;
      const isUpgradeScenario =
        hasNoActiveCheckpoints &&
        currentLevel &&
        currentLevel !== "master" &&
        ["apprentice", "professional", "coach"].includes(currentLevel);

      if (isUpgradeScenario) {
        this.showUpgradeBadgeDialog = true;
        return;
      }

      // Inspection slide-over: same as Give Badge — keep pane open; award dialog lives inside the pane.
      if (this.isInspectResModal) {
        this.inspectionAwardDialogRequest = {
          token: Date.now(),
          level: this.normalizeInspectionAwardLevel(obj),
        };
        return;
      }

      this.showAwardBadgeModal = true;
      this.awardBadgeLevel = this.normalizeInspectionAwardLevel(obj);
      if (obj?.responsibilityName) {
        this.currentSelectRes = { name: obj.responsibilityName };
      }
    },
    async awardBadge(isNotify = false) {
      // Show main loader and keep it until everything is complete
      this.$loader.show();

      try {
        const routeParams = this.$route.params;
        let responsibilityId = null;
        if (routeParams && routeParams.objId) {
          responsibilityId = routeParams.objId;
        } else if (this.currentResponsibility && this.currentResponsibility.id) {
          responsibilityId = this.currentResponsibility.id;
        }
        if (!responsibilityId) {
          throw new Error("no responsibility id available");
        }
        // Get employee ID from multiple sources to ensure we have it
        const employeeId =
          this.evalModalEmpState?.id ||
          this.employeeInfo?.id ||
          this.cachedEmpId ||
          this.$route?.params?.id ||
          this.$route?.query?.employeeId;

        if (!employeeId) {
          console.error("❌ No employee ID found for awardBadge");
          this.$notifications?.error(
            "Unable to find employee ID. Please refresh the page and try again."
          );
          this.$loader.hide();
          return;
        }

        const account = JSON.parse(localStorage.getItem("currentAccountID"));
        const data = {
          employee: employeeId,
          responsibility: responsibilityId,
          level: this.getBadgeLevelByLabel(this.awardBadgeLevel),
          given: false,
          issued_method: "awarded",
          notify: isNotify,
        };
        await createProficiencyBadge(account, data);
        await this.reloadDataAfterBadgeOperation();
        // Clear modal states after everything is complete
        this.clearModalStates();
      } catch (e) {
        console.error("❌ Error in awardBadge:", e);
        this.clearModalStates(); // Clear even on error
      } finally {
        // Hide loader only after everything is complete
        this.$loader.hide();
      }
    },

    getBadgeLevelByLabel(level) {
      switch (normalizeBadgeLevel(level)) {
        case "novice":
          return 0;
        case "apprentice":
          return 1;
        case "proficient":
          return 2;
        case "coach":
          return 3;
        case "architect":
          return 4;
        default:
          return 0;
      }
    },
    async upgradeBadge(upgradeData) {
      try {
        this.badgeData = { payload: upgradeData?.payload?.newBadge };
        await this.onClick(false);
      } catch (e) {
        console.error("Error upgrading badge:", e);
      }
    },
    handleButtonClick() {
      // Use employeeData when store's currentEmployee is not set (e.g. employee with no position yet)
      const source = this.employeeInfo || this.employeeData;
      if (!source) return;
      const { id, first_name, last_name, availability, avatar, title } = source;
      const employeeObj = {
        id,
        firstName: first_name,
        lastName: last_name,
        availability: availability ?? 1,
        picture: avatar,
        title: title,
      };
      this.currenntEmployee = structuredClone(employeeObj);
      const status = source.current_status || source.status || "draft";
      const archived = source.archived === true;
      if (status === "draft") {
        this.activateEmployee = true;
      }
      if (status === "terminated" && !archived) {
        this.reActivateEmployee = true;
      }
      if (status === "terminated" && archived) {
        this.reActivateEmployee = true;
        this.unArchiveEmployee = true;
      }
      if (status === "active") {
        this.assignRejectionMessage = "";
        this.assignEffectiveFrom = null;
        this.assignEffectiveTo = null;
        this.fetchAvailablePositions();
        this.assignPosition = true;
      }
    },
    /**
     * When the UI shows a tier but badge id was never synced (e.g. fresh give), resolve from getEmployeeBadges.
     */
    async resolveBadgePayloadForInspectionRemove() {
      const accountId = this.cachedAccountID || localStorage.getItem("currentAccountID");
      const employeeId = this.evalModalEmpState?.id || this.cachedEmpId;
      const responsibilityId = this.currentResponsibility?.id;
      if (!accountId || !employeeId || !responsibilityId) return null;
      try {
        const { data } = await getEmployeeBadges(accountId, employeeId);
        const results = data?.results || [];
        const row = this.findCurrentBadgeForResponsibility(results, responsibilityId);
        return this.badgeToInspectionPanePayload(row);
      } catch (e) {
        console.warn("resolveBadgePayloadForInspectionRemove:", e);
        return null;
      }
    },
    async processRemoveBadgeFromEvalModal(data) {
      try {
        let payload = data && typeof data === "object" ? { ...data } : { level: data };
        let id = payload.id != null && payload.id !== "" ? payload.id : null;
        if (!id) {
          const resolved = await this.resolveBadgePayloadForInspectionRemove();
          if (resolved?.id != null && resolved.id !== "") {
            id = resolved.id;
            payload = {
              ...payload,
              id: resolved.id,
              level: payload.level ?? resolved.level,
              currentBadge: payload.currentBadge ?? resolved,
            };
          }
        }
        if (!id) {
          throw new Error("badge id not found");
        }

        const badgeLevel = payload.level || payload.currentBadge?.level || "";
        const badgeLevelName = this.getBadgeLevelName(badgeLevel);

        const responsibilityName =
          this.currentSelectRes?.name || this.currentResponsibility?.name || "";

        this.selectedBadgeForRemoval = {
          id,
          badgeName: badgeLevelName,
          responsibilityName: responsibilityName,
          badgeData: payload,
        };

        this.removeBadgeDialogFromInspection = !!this.isInspectResModal;
        this.showRemoveBadgeDialog = true;
      } catch (e) {
        console.error(e);
        this.$notifications?.error("Error preparing badge removal");
      }
    },
    async handleConfirmRemoveBadge({ reason: removeReason }) {
      if (
        !this.selectedBadgeForRemoval ||
        !this.selectedBadgeForRemoval.id ||
        !removeReason?.trim()
      ) {
        this.$notifications?.error("Please provide a reason for removing the badge");
        return;
      }

      const fromInspection = this.removeBadgeDialogFromInspection;
      this.loading.removeBadge = true;
      try {
        await this.deleteBadge(this.selectedBadgeForRemoval.id, removeReason.trim(), "removed", {
          skipPostReload: true,
        });

        this.showRemoveBadgeDialog = false;
        this.selectedBadgeForRemoval = null;
        this.removeBadgeDialogFromInspection = false;

        if (fromInspection && this.evalModalEmpState) {
          this.evalModalEmpState = {
            ...this.evalModalEmpState,
            currentEmployeeBadge: null,
          };
        }

        this.$notifications?.success("Badge removed successfully");
      } catch (e) {
        console.error(e);
        this.$notifications?.error("Error removing badge");
        if (!fromInspection) {
          this.clearModalStates();
        }
        return;
      } finally {
        this.loading.removeBadge = false;
      }

      this.$loader.show();
      try {
        if (fromInspection) {
          await this.reloadDataAfterBadgeOperation();
          if (
            this.isInspectResModal &&
            this.currentResponsibility?.id &&
            this.evalModalEmpState?.id
          ) {
            await this.reloadInspectionCheckpoints(null);
            await this.loadResponsibilityCheckpoints();
          }
        } else {
          await this.reloadDataAfterChanges();
          this.clearModalStates();
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.$loader.hide();
      }
    },
    handleCloseRemoveBadgeDialog() {
      this.showRemoveBadgeDialog = false;
      this.selectedBadgeForRemoval = null;
      this.removeBadgeDialogFromInspection = false;
    },
    async deleteBadge(idBadge, reason = "", method = "", { skipPostReload = false } = {}) {
      const payload = {
        badge: idBadge,
        method: method || "string",
        reason: reason || "string",
      };
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
      await deleteResponsibilityBadge(accountID, idBadge, payload);
      if (!skipPostReload) {
        await this.reloadDataAfterBadgeOperation();
      }
    },
    onCloseModal() {
      this.activateEmployee = false;
      this.reActivateEmployee = false;
      this.unArchiveEmployee = false;
      this.assignPosition = false;
      // Clear any error messages when closing modals
      this.errorMessage = "";
    },
    dropdownItems(data) {
      const items = [];
      if (data?.data?.type === "responsibility" && !data?.data?.badge) {
        items.push({
          name: "Issue Badge",
          value: "issue_badge_action",
          icon: "plainminimalistic.svg",
          order: 2,
        });
      }
      if (data?.data?.type === "responsibility") {
        items.push({
          name: "Retire in Catalog",
          value: "retire_responsibility",
          icon: "icons/dropdown/activate.svg",
          order: 3,
        });
      }
      return items;
    },
    async reloadCheckpointsData() {
      await this.reloadDataAfterChanges();
    },
    async loadResponsibilityCheckpoints() {
      const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
      const responsibilityId = this.currentResponsibility?.id;
      if (!accountID || !responsibilityId) return;
      try {
        const response = await getResponsibilityChecklistV2(accountID, responsibilityId, {
          status: "active",
        });
        this.responsibilityCheckpoints = response?.data || [];
      } catch (error) {
        console.error("Failed to load responsibility checkpoints:", error);
        this.responsibilityCheckpoints = [];
      }
    },
    findCurrentBadgeForResponsibility(results, responsibilityId) {
      const ctx = {
        responsibilityCode:
          this.currentResponsibility?.code_str || this.currentSelectRes?.data?.data?.code || "",
      };
      return pickBadgeForResponsibility(results, responsibilityId, ctx);
    },

    badgeToInspectionPanePayload(badgeRow) {
      return mapBadgeRowToInspectionChip(badgeRow);
    },

    async syncInspectionPaneBadgeFromApi(employeeId, responsibilityId) {
      if (!employeeId || !responsibilityId) return;
      try {
        const accountId = this.cachedAccountID || localStorage.getItem("currentAccountID");
        const { data } = await getEmployeeBadges(accountId, employeeId);
        const results = data?.results || [];
        const currentBadge = this.findCurrentBadgeForResponsibility(results, responsibilityId);
        let badgePayload = this.badgeToInspectionPanePayload(currentBadge);
        const prev = this.evalModalEmpState?.currentEmployeeBadge;
        const prevLvl = prev?.level == null ? "" : String(prev.level).toLowerCase();
        if (badgePayload == null && prev && prevLvl && prevLvl !== "novice") {
          badgePayload = prev;
        }
        if (this.evalModalEmpState) {
          this.evalModalEmpState = {
            ...this.evalModalEmpState,
            currentEmployeeBadge: badgePayload,
          };
        }
        const csr = this.currentSelectRes;
        if (csr?.data?.data) {
          this.$set(csr.data.data, "badge", badgePayload);
        }
      } catch (e) {
        console.error("syncInspectionPaneBadgeFromApi:", e);
      }
    },
    async reloadInspectionCheckpoints(item) {
      const accountId = this.cachedAccountID || localStorage.getItem("currentAccountID");
      const responsibilityId = item?.responsibility_id || this.currentResponsibility?.id;
      const employeeId = item?.employee_id || this.evalModalEmpState?.id || this.cachedEmpId;
      if (!accountId || !responsibilityId || !employeeId) return;
      try {
        const response = await getListActiveCheckpointsEvalV2(
          accountId,
          responsibilityId,
          employeeId,
          100,
          0
        );
        this.processCheckpointsData(response);
        await this.syncInspectionPaneBadgeFromApi(employeeId, responsibilityId);
      } catch (error) {
        console.error("Error reloading inspection checkpoints:", error);
      }
    },
    isLevelCompleteForAward(level) {
      return isLevelCompleteForAwardCheck(level, this.checkpointsData);
    },
    getCurrentBadgeLevelNumber() {
      const currentBadge =
        this.evalModalEmpState?.currentEmployeeBadge || this.inspectionPaneEmployee?.badge;
      if (!currentBadge || currentBadge.level == null) return 0;
      if (typeof currentBadge.level === "number") return currentBadge.level;
      const label = this.getBadgeLevelStringForDialog(currentBadge.level) || "novice";
      return this.getBadgeLevelByLabel(label);
    },
    shouldShowAwardBadgeForLevel(level) {
      if (!this.isLevelCompleteForAward(level)) return false;
      const currentLevelNum = this.getCurrentBadgeLevelNumber();
      const levelStr = typeof level === "string" ? level : (level && String(level)) || "novice";
      const awardLevelNum = this.getBadgeLevelByLabel(levelStr);
      return awardLevelNum > 0 && awardLevelNum > currentLevelNum;
    },
    async handleCheckpointPass(item) {
      if (!item) return;
      try {
        this.$loader.show();
        const accountId = this.cachedAccountID || localStorage.getItem("currentAccountID");
        await passCheckPointEvaluation(accountId, item.responsibility_id, item.checkpoint_id, {
          employee: item.employee_id,
          checkpoint: item.checkpoint_id,
          reason: "passed",
          status: "passed",
        });
        await this.reloadInspectionCheckpoints(item);
        await this.$nextTick();
        let levelToShow = null;
        if (this.pendingAwardLevel) {
          const n = this.getBadgeLevelByLabel(
            typeof this.pendingAwardLevel === "string" ? this.pendingAwardLevel : "novice"
          );
          if (n > 0 && n > this.getCurrentBadgeLevelNumber()) {
            levelToShow = this.pendingAwardLevel;
          }
        } else if (item?.level && this.shouldShowAwardBadgeForLevel(item.level)) {
          levelToShow = item.level;
        }
        if (levelToShow) {
          this.showAwardBadge({
            level: levelToShow,
            responsibilityName: this.currentSelectRes?.name || this.currentResponsibility?.name,
          });
        }
        this.pendingAwardLevel = null;
      } catch (e) {
        const msg = proficiencyCheckpointErrorMessage(e) || "Error passing checkpoint";
        this.$notifications.error(msg);
        console.error("Error passing checkpoint:", e);
        this.pendingAwardLevel = null;
      } finally {
        this.$loader.hide();
      }
    },
    onCloseInspectionPane() {
      this.isInspectResModal = false;
      this.pendingAwardLevel = null;
      this.inspectionAwardDialogRequest = null;
    },
    handleCheckpointFail(item) {
      if (!item) return;
      this.pendingReasonItem = item;
      this.reasonModalType = "fail";
      this.reasonText = "";
      this.showReasonModal = true;
    },
    handleCheckpointNotApplicable(item) {
      if (!item) return;
      this.pendingReasonItem = item;
      this.reasonModalType = "notApplicable";
      this.reasonText = "";
      this.showReasonModal = true;
    },
    async handleCheckpointClose(item) {
      if (!item) return;
      this.$loader.show();
      try {
        const accountId = this.cachedAccountID || localStorage.getItem("currentAccountID");
        await resetCheckPointEvaluation(accountId, item.responsibility_id, item.checkpoint_id, {
          employee: item.employee_id,
          checkpoint: item.checkpoint_id,
          reason: "reset",
          status: "reset",
        });
        await this.reloadInspectionCheckpoints(item);
        this.$notifications?.success("Evaluation reset successfully");
      } catch (e) {
        console.error("Error resetting checkpoint:", e);
        this.$notifications?.error("Error resetting evaluation");
      } finally {
        this.$loader.hide();
      }
    },
    closeCheckpointReasonModal() {
      this.showReasonModal = false;
      this.pendingReasonItem = null;
      this.reasonText = "";
    },
    async confirmCheckpointReason() {
      const item = this.pendingReasonItem;
      const reason = this.reasonText;
      const type = this.reasonModalType;
      if (!item) return;
      this.closeCheckpointReasonModal();
      try {
        this.$loader.show();
        const accountId = this.cachedAccountID || localStorage.getItem("currentAccountID");
        if (type === "fail") {
          await failCheckPointEvaluation(accountId, item.responsibility_id, item.checkpoint_id, {
            employee: item.employee_id,
            checkpoint: item.checkpoint_id,
            reason,
            status: "failed",
          });
        } else {
          await notApplicableCheckPointEvaluation(
            accountId,
            item.responsibility_id,
            item.checkpoint_id,
            {
              employee: item.employee_id,
              checkpoint: item.checkpoint_id,
              reason,
              status: "not_applicable",
            }
          );
        }
        await this.reloadInspectionCheckpoints(item);
        await this.$nextTick();
        if (type === "fail") {
          this.pendingAwardLevel = null;
        } else {
          this.$notifications?.success("Checkpoint marked as not applicable");
          this.pendingAwardLevel = null;
        }
      } catch (e) {
        this.pendingAwardLevel = null;
        const msg = proficiencyCheckpointErrorMessage(e) || "Error evaluating checkpoint";
        this.$notifications.error(msg);
        console.error("Error evaluating checkpoint:", e);
      } finally {
        this.$loader.hide();
      }
    },
    resolveAwardLevelToShow(item) {
      if (this.pendingAwardLevel) {
        const n = this.getBadgeLevelByLabel(
          typeof this.pendingAwardLevel === "string" ? this.pendingAwardLevel : "novice"
        );
        return n > 0 && n > this.getCurrentBadgeLevelNumber() ? this.pendingAwardLevel : null;
      }
      if (item?.level && this.shouldShowAwardBadgeForLevel(item.level)) {
        return item.level;
      }
      return null;
    },
    maybeShowAwardBadgeAfterReason(item) {
      const levelToShow = this.resolveAwardLevelToShow(item);
      if (levelToShow) {
        this.showAwardBadge({
          level: levelToShow,
          responsibilityName: this.currentSelectRes?.name || this.currentResponsibility?.name,
        });
      }
    },
    async reloadDataAfterBadgeOperation() {
      this.$loader.show();
      try {
        await this.reloadDataAfterChanges();
      } catch (error) {
        console.error("[ER-9868] reloadDataAfterBadgeOperation error:", error);
      } finally {
        this.$loader.hide();
      }
    },
    async grantOrForceBadge(badgeData) {
      this.isInspectResModal = false;
      this.showBadgeConfirmationDialog = true;
      this.badgeData = badgeData;
    },
    onBadgeConfirmationKeydown(event) {
      if (event.key === "Escape") {
        this.showBadgeConfirmationDialog = false;
      }
    },
    async onClick(isNotify = false) {
      // Show main loader and keep it until everything is complete
      this.$loader.show();

      try {
        const accountID =
          this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));

        // Get employee ID from multiple sources to ensure we have it
        const employeeId =
          this.badgeData?.payload?.employee ||
          this.evalModalEmpState?.id ||
          this.employeeInfo?.id ||
          this.cachedEmpId ||
          this.$route?.params?.id ||
          this.$route?.query?.employeeId;

        if (!employeeId) {
          console.error("❌ No employee ID found for badge creation");
          this.$notifications?.error(
            "Unable to find employee ID. Please refresh the page and try again."
          );
          this.$loader.hide();
          return;
        }

        const newPayload = {
          level: this.badgeData.payload.level,
          employee: employeeId,
          given: this.badgeData.payload.given,
          responsibility: this.badgeData.payload.responsibility,
          issued_method: this.badgeData.payload.issued_method,
          notify: isNotify,
        };
        await createProficiencyBadge(accountID, newPayload);
        await this.reloadDataAfterBadgeOperation();

        // Clear modal states after everything is complete
        this.clearModalStates();
      } catch (error) {
        console.error("❌ Error in onClick:", error);
        this.clearModalStates(); // Clear even on error
      } finally {
        // Hide loader only after everything is complete
        this.$loader.hide();
      }
    },
    async loadData() {
      const empId = this.cachedEmpId || this.$route?.params?.id;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
      this.$loader.show();

      try {
        await this.getEmpAssignments(accountID, empId);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        this.$loader.hide();
      }
    },
    async loadAllCheckpointsEvaluationData(accountId, employeeId, responsibilityId) {
      const response = await getListActiveCheckpointsEval(accountId, responsibilityId, employeeId);
      return response.data;
    },
    async getEmpAssignments(accountId, empId, { force = false } = {}) {
      // Prevent duplicate calls unless a forced refresh is requested
      if (this.isLoadingData && !force) {
        return;
      }

      this.isLoadingData = true;
      try {
        const { data } = await getEmployeeAssignments(
          accountId,
          empId,
          this.pagination.limit,
          this.pagination.offset,
          "active"
        );

        if (data) {
          this.pagination.total = data.count || 0;

          if (data.results?.length > 0) {
            this.dataSource = this.transformData(data.results);
          } else {
            this.dataSource = [];
          }
        }
      } finally {
        this.isLoadingData = false;
      }
    },
    async onEmployeeReadiness() {
      const empId = this.cachedEmpId || this.$route?.params?.id;
      if (empId) {
        this.$parent.$refs?.employeeSideBar?.recalculateEmployeeReadiness(empId);

        const employeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];
        if (employeeData) {
          this.employeeData = employeeData;
        }
      }
    },
    getTreeRowReadiness(itemId) {
      const item = this.dataSource.find((row) => row.id === itemId);
      if (!item) return null;
      const value = item.readiness ?? item.data?.readiness;
      return value == null ? null : value;
    },
    hasReadinessUpdatedSinceBaseline(baseline, freshEmployee) {
      if (!baseline) return true;

      // Importance changes update the employee header first. The affected
      // row can still be stale, so row readiness is the source of truth
      // when we know which item changed.
      if (baseline.itemId != null && baseline.itemReadiness != null) {
        const itemReadiness = this.getTreeRowReadiness(baseline.itemId);
        return itemReadiness != null && itemReadiness !== baseline.itemReadiness;
      }

      const employeeReadiness =
        freshEmployee?.primary_readiness ?? freshEmployee?.current_readiness ?? null;
      return (
        baseline.employeeReadiness != null &&
        employeeReadiness != null &&
        employeeReadiness !== baseline.employeeReadiness
      );
    },
    waitForReadinessPollInterval(intervalMs = 1500) {
      return new Promise((resolve) => setTimeout(resolve, intervalMs));
    },
    async pollAssignmentsUntilReadinessUpdates(accountID, empId, baseline) {
      const timeoutMs = 30000;
      const intervalMs = 1500;
      const start = Date.now();
      let latestEmployee = null;

      while (Date.now() - start < timeoutMs) {
        await recalculateEmployeeReadiness(accountID, empId).catch(() => null);

        try {
          await this.getEmpAssignments(accountID, empId, { force: true });
        } catch (error) {
          console.error("Error refreshing assignments during readiness poll:", error);
        }

        try {
          await this.refreshExpandedChildData(accountID, empId, { manageLoading: false });
        } catch (error) {
          console.error("Error refreshing expanded readiness rows during poll:", error);
        }

        try {
          latestEmployee = await this.employeeStore.fetchCurrentEmployee(empId, true);
        } catch (error) {
          latestEmployee = null;
        }

        if (latestEmployee) {
          this.employeeData = { ...latestEmployee };
        }

        if (this.hasReadinessUpdatedSinceBaseline(baseline, latestEmployee)) {
          return latestEmployee;
        }

        await this.waitForReadinessPollInterval(intervalMs);
      }

      return latestEmployee;
    },
    async reloadDataAfterChanges(options = {}) {
      const { afterImportanceChange = false, baseline = null } = options;
      const empId = this.cachedEmpId || this.$route.params.id || this.$route.query.employeeId;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));

      try {
        this.loading.readinessBar = true;
        this.preserveExpansionState();

        if (afterImportanceChange) {
          const polledEmployee = await this.pollAssignmentsUntilReadinessUpdates(
            accountID,
            empId,
            baseline
          );
          if (polledEmployee) {
            this.$store.commit("employeeStore/setCurrentEmployee", polledEmployee);
            this.employeeStore.setCurrentEmployee(polledEmployee);
            this.employeeData = { ...polledEmployee };
            await this.$nextTick();
          }
        } else {
          const recalcResult = await recalculateEmployeeReadiness(accountID, empId);
          let recalcReadiness = null;
          if (recalcResult?.data?.current_readiness != null) {
            recalcReadiness = recalcResult.data.current_readiness;
            console.log("[ER-9868] reloadDataAfterChanges recalcReadiness:", recalcReadiness);
            if (this.employeeData) {
              this.employeeData.current_readiness = recalcReadiness;
            }
          }

          // Force refresh employee data from API to get latest position assignments
          const freshEmployeeData = await this.employeeStore.fetchCurrentEmployee(
            empId,
            true // forceRefresh
          );

          if (freshEmployeeData) {
            console.log(
              "[ER-9868] fetchCurrentEmployee (reloadDataAfterChanges) current_readiness:",
              freshEmployeeData.current_readiness
            );
            // Preserve recalculated readiness — fetchCurrentEmployee may return stale value
            if (recalcReadiness != null) {
              freshEmployeeData.current_readiness = recalcReadiness;
            }
            console.log(
              "[ER-9868] final employeeData.current_readiness set to:",
              freshEmployeeData.current_readiness
            );
            // Update stores with fresh data
            this.$store.commit("employeeStore/setCurrentEmployee", freshEmployeeData);
            this.employeeStore.setCurrentEmployee(freshEmployeeData);
            // Update local employeeData with fresh data
            this.employeeData = { ...freshEmployeeData };
            await this.$nextTick();
          } else {
            // Fallback to getEmployeeData if fetchCurrentEmployee fails
            await this.getEmployeeData();
            const employeeData =
              this.employeeStore.currentEmployee ||
              this.$store.getters["employeeStore/currentEmployee"];
            if (employeeData) {
              this.$set(this, "employeeData", { ...employeeData });
            }
          }
        }

        this.$parent.$refs?.employeeSideBar?.recalculateEmployeeReadiness(empId);

        const weeklyDataResponse = await this.fetchWeeklyReadinessData();
        if (weeklyDataResponse) {
          this.weeklyReadinessData = weeklyDataResponse;
        }

        if (!this.isTerminatedEmployee(this.employeeData) && !this.$route.path.includes("badges")) {
          if (!afterImportanceChange) {
            await this.getEmpAssignments(accountID, empId);
            await this.refreshExpandedChildData(accountID, empId);
          }
          this.treeListKey++;

          this.$nextTick(() => {
            this.$nextTick(() => {
              this.restoreExpansionState();
            });
          });
        } else if (this.isTerminatedEmployee(this.employeeData)) {
          // Clear dataSource for terminated employees
          this.dataSource = [];
          this.pagination.total = 0;
          this.treeListKey++;
        }
      } catch (error) {
        console.error("Error reloading data:", error);
      } finally {
        this.loading.readinessBar = false;
      }
    },

    transformData(dataArray) {
      const result = [];
      const today = new Date();

      dataArray.forEach((data) => {
        const positionObject = this.transformPositionData(data, today);
        result.push(positionObject);
      });

      // Sort the results by commitment level
      const commitmentOrder = {
        Primary: 1,
        "Preparing for": 2,
        Backup: 3,
      };

      result.sort((a, b) => {
        const aCommit = a.data?.commitmentLevel || "";
        const bCommit = b.data?.commitmentLevel || "";
        return (commitmentOrder[aCommit] || 99) - (commitmentOrder[bCommit] || 99);
      });

      return result;
    },

    transformPositionData(data, today) {
      const isNew =
        new Date(data.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      // Direct-attached Responsibility Assignments (Rule 1/12 — ER-11412/ER-11413):
      // the position-listing payload may carry a count (or the rows themselves)
      // alongside role_assignment_count. Read defensively so a pre-ER-11412 API
      // response behaves exactly as before.
      const directResponsibilityCount =
        data.direct_responsibility_assignments_count ??
        data.direct_responsibility_assignments?.length ??
        0;

      return {
        id: `position_${data.id}`, // Make position IDs unique
        parentId: -1, // Root-level has no parent
        data: {
          type: data.assignment_type,
          commitmentLevel: data.commitment_level,
          assignmentType: data.assignment_type,
          importanceLevel: data.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: data.position_code,
          name: data.position_name,
          isNew: isNew,
          isAccepted: false,
          acceptanceStatus: "pending",
          // Add expandable indicator if roles exist
          hasRoles: data.role_assignment_count > 0,
          roleCount: data.role_assignment_count,
          // Direct responsibilities make the Position expandable too, so rows
          // with only direct children (no Role rows) still render + expand.
          hasDirectResponsibilities: directResponsibilityCount > 0,
          directResponsibilityCount,
          readiness: data.current_readiness,
        },
        assignment_id: data.id,
        status: data.status,
        readiness: data.current_readiness,
      };
    },

    transformRoleData(role, parentId, today) {
      const isRoleNew =
        new Date(role.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      // Map API status to acceptanceStatus for AcceptanceStatusBadge component
      // AcceptanceStatusBadge only supports: "pending", "accepted", "rejected"
      // For non-active statuses (terminated, relieved, detached, etc.), don't show acceptance status badge
      // For active roles, check if they're new (pending) or accepted
      let acceptanceStatus = null;
      if (role.status === "active") {
        acceptanceStatus = isRoleNew ? "pending" : "accepted";
      }
      // For terminated/relieved/detached/etc., acceptanceStatus remains null so badge won't show
      // The actual status will be displayed via the status field

      return {
        id: `role_${role.id}`, // Make role IDs unique
        parentId: parentId, // Set parent ID to main object
        data: {
          type: role.assignment_type,
          commitmentLevel: role.commitment_level,
          assignmentType: role.assignment_type,
          importanceLevel: role.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: role.role_code,
          name: role.role_name,
          isNew: isRoleNew,
          isAccepted: !isRoleNew && role.status === "active",
          acceptanceStatus: acceptanceStatus,
          assignment_id: parentId,
          // Add expandable indicator if responsibilities exist
          hasResponsibilities: role.responsibility_assignment_count > 0,
          responsibilityCount: role.responsibility_assignment_count,
          // Include additional fields from API
          assignedOn: role.assigned_on,
          removedOn: role.removed_on,
          assignedBy: role.assigned_by,
          removedBy: role.removed_by,
          reason: role.reason,
          readiness: role.current_readiness,
        },
        assignment_id: role.id,
        status: role.status || "active", // Use actual status from API (terminated, relieved, detached, active, etc.)
        readiness: role.current_readiness,
      };
    },

    transformResponsibilityData(resp, parentId, assignmentId, today) {
      let checkpoints = [];
      const isRespNew =
        new Date(resp.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      return {
        id: `responsibility_${resp.id}`, // Make responsibility IDs unique
        parentId: parentId, // Correctly set parent ID
        data: {
          type: resp.assignment_type,
          commitmentLevel: resp.commitment_level,
          assignmentType: resp.assignment_type,
          importanceLevel: resp.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: resp.responsibility_code,
          name: resp.responsibility_name,
          isNew: isRespNew,
          isAccepted: isRespNew,
          acceptanceStatus: "pending",
          assignment_id: assignmentId,
          badge: resp.badge,
          responsibility_id: 1310,
          readiness: resp.current_readiness,
        },
        checkpointsData: checkpoints,
        status: resp.status,
        readiness: resp.current_readiness,
      };
    },

    transformResponsibilityDataFromAPI(resp, parentId, assignmentId, today) {
      const checkpoints = [];
      const isRespNew =
        new Date(resp.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      let badge = null;
      if (resp.badge_level !== null && resp.badge_status !== "expired") {
        badge = {
          id: resp.badge_id,
          level: resp.badge_level,
          status: resp.badge_issued_method,
          expiration_date: resp.badge_expiration_date,
          issued_method: resp.badge_issued_method,
        };
      }

      // Extract checkpoint counts from API response
      const checkpointCounts = {
        apprentice_checkpoint_count: resp.apprentice_checkpoint_count || 0,
        professional_checkpoint_count: resp.professional_checkpoint_count || 0,
        coach_checkpoint_count: resp.coach_checkpoint_count || 0,
        master_checkpoint_count: resp.master_checkpoint_count || 0,
        apprentice_passed_checkpoint_count: resp.apprentice_passed_checkpoint_count || 0,
        professional_passed_checkpoint_count: resp.professional_passed_checkpoint_count || 0,
        coach_passed_checkpoint_count: resp.coach_passed_checkpoint_count || 0,
        master_passed_checkpoint_count: resp.master_passed_checkpoint_count || 0,
        active_checkpoints_count:
          resp.apprentice_checkpoint_count +
          resp.professional_checkpoint_count +
          resp.coach_checkpoint_count +
          resp.master_checkpoint_count,
      };
      // Map API status to acceptanceStatus for AcceptanceStatusBadge component
      // AcceptanceStatusBadge only supports: "pending", "accepted", "rejected"
      // For non-active statuses (terminated, relieved, detached, etc.), don't show acceptance status badge
      // For active responsibilities, check if they're new (pending) or accepted
      let acceptanceStatus = null;
      if (resp.status === "active") {
        acceptanceStatus = isRespNew ? "pending" : "accepted";
      }
      // For terminated/relieved/detached/etc., acceptanceStatus remains null so badge won't show
      // The actual status will be displayed via the status field

      // Map available_inspectors to avatars format for display
      // Only show inspectors if employee doesn't have Master badge level (they can inspect themselves)
      const avatars =
        resp.badge_level === "Master" || resp.current_badge_level === "Master"
          ? []
          : (resp.available_inspectors || []).map((inspector) => {
              // Split full_name into firstName and lastName
              const nameParts = (inspector.full_name || "").trim().split(/\s+/);
              const firstName = nameParts[0] || "";
              const lastName = nameParts.slice(1).join(" ") || "";

              // Try to find badge level from badge_holders by matching name
              const badgeHolder = (resp.badge_holders || []).find((bh) => {
                const badgeHolderFullName = `${bh.first_name || ""} ${bh.last_name || ""}`.trim();
                return badgeHolderFullName === (inspector.full_name || "").trim();
              });

              return {
                id: inspector.id,
                badgeLevel: badgeHolder?.badge_level || null,
                firstName: firstName,
                lastName: lastName,
                fullName: inspector.full_name || "",
                title: badgeHolder?.title || null,
                master_will_coach: badgeHolder?.master_will_coach || null,
                img: inspector.avatar || "",
                isHoverEnabled: !!inspector.avatar,
              };
            });

      return {
        id: `responsibility_${resp.id}`, // Make responsibility IDs unique
        parentId: parentId, // Correctly set parent ID
        data: {
          type: resp.assignment_type,
          commitmentLevel: resp.commitment_level,
          assignmentType: resp.assignment_type,
          importanceLevel: resp.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: resp.responsibility_code,
          name: resp.responsibility_name,
          isNew: isRespNew,
          isAccepted: !isRespNew && resp.status === "active",
          acceptanceStatus: acceptanceStatus,
          assignment_id: assignmentId,
          badge: badge,
          responsibility_id: resp.responsibility,
          checkpointCounts: checkpointCounts,
          avatars: avatars, // Available inspectors mapped to avatars format
        },
        checkpointsData: checkpoints,
        status: resp.status || "active", // Use actual status from API (terminated, relieved, detached, active, etc.)
        readiness: resp.current_readiness,
      };
    },

    transformDirectResponsibilityDataFromAPI(resp, positionAssignmentId, today) {
      // Rule 12: a direct-sourced Responsibility Assignment hangs straight off
      // the Position (parentId position_<id>) — no Role in between. Reuse the
      // standard Responsibility row transform so the card renders identically
      // (checkpoints/badges/inspectors), then relabel the linkage and mark the
      // row so click-through/breadcrumb navigation can stop at the Position.
      const row = this.transformResponsibilityDataFromAPI(
        resp,
        `position_${positionAssignmentId}`,
        positionAssignmentId,
        today
      );
      row.data.directResponsibility = true;
      return row;
    },

    async getAllResponsibilities() {
      try {
        const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
        const data = await getListofResponsibility(accountID);
        this.allResponsibilities = data.data.results;
      } catch (error) {
        console.error("Error fetching responsibilities:", error);
      }
    },
    async onTreeExpandRole({ assignmentId, rowType, expandedRowData }) {
      const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
      const empId = this.$route.params.id || this.$route.query.employeeId;

      // Check if data is already loaded for this expansion
      const cacheKey = `${rowType}_${assignmentId}`;
      if (this.loadedChildData.has(cacheKey)) {
        return;
      }

      // Remove skeleton loaders using the correct parent ID format
      const parentId = rowType === "position" ? `position_${assignmentId}` : expandedRowData.id;
      this.removeSkeletonLoaders(parentId);

      try {
        if (rowType === "position") {
          await this.handlePositionExpansion(accountID, empId, assignmentId);
          // Mark this position's roles as loaded
          this.loadedChildData.set(cacheKey, true);
        } else if (rowType === "role") {
          await this.handleRoleExpansion(accountID, empId, assignmentId, expandedRowData);
          // Mark this role's responsibilities as loaded
          this.loadedChildData.set(cacheKey, true);
        }
      } catch (error) {
        console.error("Error loading assignments:", error);
        // Remove skeleton loaders using the correct parent ID format
        const parentId = rowType === "position" ? `position_${assignmentId}` : expandedRowData.id;
        this.removeSkeletonLoaders(parentId);
      }
    },

    async updatePositionHasRoles(accountID, positionId, positionAssignmentId) {
      try {
        // Call the position API to get position details including roles_count
        const response = await getPositionId(accountID, positionId);

        // Get roles_count from position details
        const rolesCount = response.data?.roles_count || 0;
        const hasRoles = rolesCount > 0;

        // Find the position in dataSource and update its hasRoles property
        const positionIndex = this.findPositionIndex(positionAssignmentId);

        if (positionIndex !== -1) {
          // Update the position data to show it has roles
          this.$set(this.dataSource[positionIndex].data, "hasRoles", hasRoles);
          this.$set(this.dataSource[positionIndex].data, "roleCount", rolesCount);

          // Force TreeList to update by incrementing key
          this.treeListKey++;
        }
      } catch (error) {
        console.error("Error fetching position details:", error);
      }
    },

    async handlePositionExpansion(accountID, empId, positionAssignmentId) {
      const positionKey = `position_${positionAssignmentId}`;

      // Treat Role rows and direct Responsibility rows as one set of Position
      // children, so a re-expand does not duplicate either kind (ER-11413).
      const existingChildren = this.dataSource.filter(
        (item) =>
          item.parentId === positionKey &&
          (item.data?.type === "role" || item.data?.type === "responsibility") &&
          !item.data?.isSkeletonLoader
      );

      if (existingChildren.length > 0) {
        return;
      }

      const positionData = this.dataSource.find((item) => item.id === positionKey);
      const roleCount = positionData?.data?.roleCount || 0;
      const directResponsibilityCount = positionData?.data?.directResponsibilityCount || 0;
      const childCount = roleCount + directResponsibilityCount;

      this.addSkeletonLoaders(positionKey, "position", childCount || 2);

      // Fire the Role fetch always (existing behaviour). The direct-sourced
      // fetch is fired only when the Position listing signalled direct rows
      // (ER-11412 field on the payload): on the pre-ER-11412 API the
      // responsibility-assignment endpoint has no position_assignment filter,
      // so an unconditional call would return every employee Responsibility
      // and duplicate the Role children (ER-11413 regression guard).
      const [roleResponse, directResponse] = await Promise.all([
        getRoleAssignment(
          accountID,
          empId,
          positionAssignmentId,
          null // Fetch all statuses (active, terminated, relieved, etc.)
        ),
        directResponsibilityCount > 0
          ? this.fetchDirectResponsibilityAssignments(accountID, empId, positionAssignmentId)
          : Promise.resolve(null),
      ]);

      this.removeSkeletonLoaders(positionKey);

      const today = new Date();
      const transformedChildren = [];

      const roleResults = roleResponse?.data?.results || [];
      const directResults = directResponse?.data?.results || [];

      if (roleResults.length > 0 || directResults.length > 0) {
        // Rule 12: Role rows and direct Responsibility rows are siblings under
        // the Position; the shared tree list sorts each parent's children by
        // importance (Rule 12a).
        transformedChildren.push(
          ...roleResults.map((role) => this.transformRoleData(role, positionKey, today)),
          ...directResults.map((resp) =>
            this.transformDirectResponsibilityDataFromAPI(resp, positionAssignmentId, today)
          )
        );

        this.dataSource = [...this.dataSource, ...transformedChildren];

        const positionIndex = this.findPositionIndex(positionAssignmentId);
        if (positionIndex !== -1) {
          this.$set(this.dataSource[positionIndex].data, "hasRoles", roleResults.length > 0);
          this.$set(this.dataSource[positionIndex].data, "roleCount", roleResults.length);
          this.$set(
            this.dataSource[positionIndex].data,
            "hasDirectResponsibilities",
            directResults.length > 0
          );
          this.$set(
            this.dataSource[positionIndex].data,
            "directResponsibilityCount",
            directResults.length
          );
        }
      }
    },

    // Direct-attached (Position-level) Responsibility Assignments come from the
    // ER-11412 backend surface. The endpoint may not be deployed yet on every
    // environment, so degrade to "no direct rows" instead of failing the whole
    // Position expansion when the call is not available. Rule 1 partitions the
    // two shapes by role_assignment null, which the API serializes as an absent
    // role relation — role_name/role_code come back as null, undefined, or an
    // empty object `{}` (exIQtive's "empty relation" convention) — never as a
    // real string. Keep only rows whose role linkage is not a populated string,
    // so an endpoint that has not yet added the position_assignment filter can
    // never leak Role-sourced rows in here.
    async fetchDirectResponsibilityAssignments(accountID, empId, positionAssignmentId) {
      try {
        const response = await getDirectResponsibilityAssignment(
          accountID,
          empId,
          positionAssignmentId,
          "active"
        );
        const directResults = (response?.data?.results || []).filter(
          (row) => typeof row.role_name !== "string" && typeof row.role_code !== "string"
        );
        return { data: { results: directResults } };
      } catch (error) {
        console.error("Error fetching direct responsibility assignments:", error);
        return { data: { results: [] } };
      }
    },

    async handleRoleExpansion(accountID, empId, roleAssignmentId, expandedRowData) {
      const existingResponsibilities = this.dataSource.filter(
        (item) =>
          item.parentId === expandedRowData.id &&
          item.data?.type === "responsibility" &&
          !item.data?.isSkeletonLoader
      );

      if (existingResponsibilities.length > 0) {
        return;
      }

      const responsibilityCount = expandedRowData.data?.responsibilityCount || 3; // fallback to 3 if not found

      this.addSkeletonLoaders(expandedRowData.id, "role", responsibilityCount);

      const response = await getResponsibilityAssignment(
        accountID,
        roleAssignmentId,
        "active",
        "active"
      );

      this.removeSkeletonLoaders(expandedRowData.id);

      if (response.data && response.data.results && response.data.results.length > 0) {
        const today = new Date();
        const responsibilityData = response.data.results;

        const transformedResponsibilities = responsibilityData.map((resp) =>
          this.transformResponsibilityDataFromAPI(resp, expandedRowData.id, roleAssignmentId, today)
        );

        // Add transformed responsibilities to dataSource
        this.dataSource = [...this.dataSource, ...transformedResponsibilities];

        // Update the parent role to show it has responsibilities
        const roleIndex = this.dataSource.findIndex((item) => item.id === expandedRowData.id);
        if (roleIndex !== -1) {
          this.dataSource[roleIndex].data.hasResponsibilities = true;
          this.dataSource[roleIndex].data.responsibilityCount = transformedResponsibilities.length;
        }
      }
    },
    onOptionClick(item) {
      if (this.inlineInspectEnabled && item?.action === "Inspect") {
        this.openInlineInspect(item);
        return;
      }
      if (item?.item) {
        switch (item.item.value) {
          case "issue_badge_action":
            this.onIssueBadge(item);
            break;
          case "retire_responsibility":
            this.showRetireResponsibilityDialog = true;
            this.currentResponsibility = item.data.data;
            break;
          default:
            break;
        }
      } else {
        this.onIssueBadge(item);
      }
    },
    async retireResponsibility() {
      this.$loader.show();
      try {
        const id = this.currentResponsibility?.responsibility_id;
        const name = this.currentResponsibility?.name;

        const account_id =
          this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
        if (!id || !name) {
          throw new Error("data missing error");
        }
        const payload = {
          status: "retired",
          name: this.currentResponsibility?.name,
          account: account_id,
        };
        await updateRetireResponsibility(account_id, id, payload);
        this.showRetireResponsibilityDialog = false;
        // Immediately fetch fresh employee data from API to update status
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          this.employeeInfo?.id,
          true
        );
        if (employeeData) {
          // Update store and local data with fresh employee status
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeData = employeeData;
        }

        // Use reloadDataAfterChanges instead of loadData to properly clear cache
        // and refresh tree expansion state
        await this.reloadDataAfterChanges();
      } catch (e) {
        console.error("Error retiring responsibility:", e);
      } finally {
        this.$loader.hide();
      }
    },
    async onIssueBadge(item) {
      this.$loader.show();
      const emp_id = this.$route.params.id || this.$route.query.employeeId;
      let response = null;
      let badgesResponse = null;

      try {
        [response, , badgesResponse] = await Promise.all([
          getListActiveCheckpointsEvalV2(
            this.cachedAccountID,
            item.data.data.responsibility_id,
            emp_id,
            item.data.data?.checkpointCounts?.active_checkpoints_count,
            0
          ),
          getAssignmentEval(
            this.cachedAccountID,
            item.data.data.responsibility_id,
            "responsibility"
          ),
          getEmployeeBadges(this.cachedAccountID, emp_id),
        ]);
      } catch (err) {
        console.error("Inspect APIs failed:", err);
        this.processCheckpointsData({ data: {} });
        if (item?.data?.data) this.setResponsibilityAndCounts(item);
        await this.setupInspectModal(item, null, emp_id);
        this.$loader.hide();
        this.$loader.setAppLoading(false);
        return;
      }

      this.$loader.hide();

      this.processBadgesResponse(badgesResponse);
      this.processCheckpointsData(response);
      this.setResponsibilityAndCounts(item);

      const hasActiveStatus = this.checkpointsData.length > 0;

      if (hasActiveStatus) {
        // ER-11331: route into the newer coach inspection screen instead of the
        // legacy Checkpoint/Status table (EvalResponsibilityModal/CheckpointsTable).
        // ER-11639: stay on the Organization employee record so the destination
        // is not indistinguishable from Your Stuff > Coach.
        const { emp, employeeName } = this.resolveInspectEmployee();
        this.stashOrganizationInspectionContext(item, emp_id, emp, employeeName);
        this.$router.push({
          path: `/organization/employees/${emp_id}/inspection`,
          query: {
            responsibilityId: item.data.data.responsibility_id,
            from: "organization",
            employeeName,
          },
        });
      } else {
        this.handleNoActiveCheckpoints(item, emp_id);
      }
      this.$loader.setAppLoading(false);
    },
    buildOrganizationInspectionContext(item, empId, emp, employeeName) {
      const isDirectResponsibility = item?.data?.data?.directResponsibility === true;
      const parentNode = this.dataSource.find((n) => n.id === item?.data?.parentId);
      const roleNode = isDirectResponsibility ? null : parentNode;
      const posNode = isDirectResponsibility
        ? parentNode
        : this.dataSource.find((n) => n.id === parentNode?.parentId);
      const displayCode = (code) => this.extractCode(code) || code || "";
      const row = item?.data?.data || {};
      return {
        employeeId: empId,
        employeeName,
        firstName: emp?.first_name || "",
        lastName: emp?.last_name || "",
        avatar: emp?.avatar || "",
        email: emp?.email || "",
        jobTitle:
          emp?.title ||
          emp?.primary_position?.name ||
          emp?.primary_positions?.[0]?.name ||
          posNode?.data?.name ||
          "",
        access: Object.hasOwn(emp || {}, "access") ? emp.access : undefined,
        primaryPositionName: emp?.primary_position?.name || emp?.primary_positions?.[0]?.name || "",
        preparingForPositionName:
          emp?.preparing_for_position?.name || emp?.preparing_for_positions?.[0]?.name || "",
        responsibilityId: row.responsibility_id,
        badge: row.badge || null,
        checkpointCounts: row.checkpointCounts || {},
        responsibility: {
          responsibilityCode: displayCode(row.code),
          responsibilityName: row.name ?? "",
          responsibilityImportance: row.importanceLevel ?? row.importance ?? 3,
          roleCode: isDirectResponsibility ? "" : displayCode(roleNode?.data?.code),
          roleName: isDirectResponsibility ? "" : roleNode?.data?.name ?? "",
          roleImportance: roleNode?.data?.importanceLevel ?? 3,
          positionCode: displayCode(posNode?.data?.code),
          positionName: posNode?.data?.name ?? "",
          positionImportance: posNode?.data?.importanceLevel ?? 3,
          positionCommitment: row.commitmentLevel || posNode?.data?.commitmentLevel || "Primary",
          assignedOn: row.assigned_on || row.assignedOn || null,
          newDuration: 15,
          show_codes: true,
        },
      };
    },
    stashOrganizationInspectionContext(item, empId, emp, employeeName) {
      try {
        sessionStorage.setItem(
          "exiqtive.organizationInspectionContext",
          JSON.stringify(this.buildOrganizationInspectionContext(item, empId, emp, employeeName))
        );
      } catch (e) {
        console.error("Failed to stash Organization inspection context:", e);
      }
    },
    resolveInspectEmployee() {
      const emp =
        this.employeeData ||
        this.employeeDataWithBadges ||
        this.employeeInfo ||
        this.$store?.getters?.["employeeStore/currentEmployee"];
      const employeeName =
        emp?.first_name && emp?.last_name ? `${emp.first_name} ${emp.last_name}` : emp?.name || "";
      return { emp, employeeName };
    },
    async openInlineInspect(item) {
      const empId = this.cachedEmpId || this.$route.params.id || this.$route.query.employeeId;
      const { emp, employeeName } = this.resolveInspectEmployee();
      const context = this.buildOrganizationInspectionContext(item, empId, emp, employeeName);
      const row = buildInspectionRowFromOrganizationContext(context);
      if (!row) return;
      try {
        await this.inspectCtx.openInspection(row);
      } catch (e) {
        console.error("Failed to open inline inspection:", e);
        this.$notifications?.error("Unable to open the inspection checklist");
      }
    },
    closeInlineInspect(breadcrumb) {
      if (breadcrumb?.disabled) return;
      this.inspectCtx.closeInspectChecklistView();
    },
    async onInlineInspectClosed() {
      await this.reloadDataAfterBadgeOperation();
    },
    handleNoActiveCheckpoints(item, emp_id) {
      // No active checkpoints: either "Give" (Novice) or "Give Upgrade" (has badge, not Master)
      const currentBadge = item?.data?.data?.badge;
      let currentLevelRaw = null;
      if (currentBadge?.level != null) {
        currentLevelRaw =
          typeof currentBadge.level === "string"
            ? currentBadge.level
            : this.getBadgeLevelStringForDialog(currentBadge.level);
      }
      const currentLevel = currentLevelRaw ? currentLevelRaw.toLowerCase() : null;
      const isUpgradeScenario =
        currentLevel &&
        currentLevel !== "master" &&
        ["apprentice", "professional", "coach"].includes(currentLevel);

      this.setupForceBadgeScenario(item, emp_id);
      if (isUpgradeScenario) {
        this.isForceBadge = false;
        this.showUpgradeBadgeDialog = true;
      }
    },
    processBadgesResponse(badgesResponse) {
      if (badgesResponse?.data?.results) {
        const currentEmployeeData = this.$store.getters["employeeStore/currentEmployee"];
        if (currentEmployeeData) {
          this.employeeDataWithBadges = {
            ...currentEmployeeData,
            badges: badgesResponse.data.results,
            badgeList: badgesResponse.data.results,
          };
        }
      }
    },
    processCheckpointsData(response) {
      const raw = response?.data?.results ?? response?.data ?? [];
      const list = Array.isArray(raw) ? raw : [];
      const toTitleCaseStatus = (status) => {
        if (!status) return "Pending";
        return status.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
      };
      // The evaluation API exposes the outcome as `inspection_status` on newer
      // records and `status`/`evaluation_status` on older ones. Prefer whichever
      // is present so a passed checkpoint is not shown as Pending.
      const resolveOutcome = (el) =>
        el.status || el.inspection_status || el.evaluation_status || null;
      this.checkpointsData = list.map((el) => {
        const outcome = resolveOutcome(el);
        return {
          id: el.checkpoint,
          // Inspection PK — the record checkpoint_evaluation chatter is keyed on.
          evaluation_id: el.id,
          checkpoint_id: el.checkpoint,
          responsibility_id: el.responsibility,
          employee_id: el.employee,
          level: this.getProficiencyLevelNameFromNumber(el),
          prefix: el.prefix || "",
          checkpoint: el.checkpoint_criteria || "",
          criteria: el.checkpoint_criteria,
          instructions: el.checkpoint_instructions,
          status: toTitleCaseStatus(outcome),
          evaluation_status: outcome,
          change_evaluation_status: this.updateChangeEvaluationStatus(el),
          inspected_by: el.evaluated_by?.name || null,
          inspected_by_avatar: el.evaluator_avatar || null,
          inspected_on: el.evaluated_on || el.inspected_on || el.inspection_attempted_on || null,
          reason: el.reason || null,
          microlearning: el.microlearning || null,
          microlearning_video_id: el.microlearning_video_id || null,
          failed_by: el.evaluated_by?.name || null,
          failed_on: el.evaluated_on || el.inspected_on || null,
          failed_reason: el.reason,
          reset_by: el.reset_by,
          reset_on: el.reset_on || "",
          reset_reason: "",
          updated_on: el.evaluated_on || el.inspected_on || null,
          updated_by: {
            ...el.evaluated_by,
            picture: el.evaluator_avatar,
          },
          avatar: el.evaluator_avatar,
        };
      });
    },
    setResponsibilityAndCounts(item) {
      this.currentSelectRes = item;
      const badge = item.data?.data?.badge;
      const levelRaw = badge?.level;
      let level = "apprentice";
      if (levelRaw != null) {
        if (typeof levelRaw === "string") {
          level = levelRaw.toLowerCase();
        } else {
          level = (this.getBadgeLevelStringForDialog(levelRaw) || "").toLowerCase();
        }
      }
      const validLevels = ["apprentice", "professional", "coach", "master"];
      const validatedLevel = validLevels.includes(level) ? level : "apprentice";
      this.currentResponsibility = {
        id: item.data.data.responsibility_id,
        name: item.data?.data?.name,
        code_str: item.data?.data?.code,
        code: this.extractCode(item.data?.data?.code),
        level: validatedLevel,
      };

      this.currentCheckpointCounts = item.data?.data?.checkpointCounts || {
        apprentice_checkpoint_count: 0,
        professional_checkpoint_count: 0,
        coach_checkpoint_count: 0,
        master_checkpoint_count: 0,
        apprentice_passed_checkpoint_count: 0,
        professional_passed_checkpoint_count: 0,
        coach_passed_checkpoint_count: 0,
        master_passed_checkpoint_count: 0,
      };
    },
    /**
     * Preserve API `access: null` for Give/Award notify UI (do not default to a fake access object).
     */
    resolvePathNotifyAccess(...sources) {
      const src = sources.find(
        (s) => s != null && typeof s === "object" && Object.hasOwn(s, "access")
      );
      return src ? src.access : null;
    },
    buildEvalModalCurrentEmployeeForAwardDialog() {
      const e = this.evalModalEmpState;
      if (!e) return null;
      return {
        user: e.id || e.user,
        access: Object.hasOwn(e, "access") ? e.access : null,
      };
    },
    async setupInspectModal(item, evaluateData, emp_id) {
      this.showEmployeeIssueBadge = false;
      this.isForceBadge = false;
      this.employeeDataWithBadges = null;
      this.inspectionCurrentCheckpoint = 0;

      const currentEmployeeData = this.$store.getters["employeeStore/currentEmployee"];

      // Rule 12 (ER-11413): a direct-sourced Responsibility Assignment hangs
      // straight off the Position — its tree parent is the Position row, not a
      // Role. Treat the parent as the Position (posNode) and leave the Role
      // slot empty so the pane breadcrumb ends at the Position, instead of
      // mislabelling the Position row as a Role.
      const isDirectResponsibility = item?.data?.data?.directResponsibility === true;
      const parentNode = this.dataSource.find((n) => n.id === item.data.parentId);
      const roleNode = isDirectResponsibility ? null : parentNode;
      const posNode = isDirectResponsibility
        ? parentNode
        : this.dataSource.find((n) => n.id === parentNode?.parentId);

      // Codes can be numeric ("#77") or lettered ("#R77", "#RS700", "#P7").
      // extractCode() only pulls `#<digits>`, so fall back to the raw code —
      // same convention as the role/responsibility lookup at line ~977.
      const displayCode = (code) => this.extractCode(code) || code || "";
      this.evalModalHeaderState = {
        responsibilityCode: displayCode(item.data.data.code),
        responsibilityName: item.data.data.name ?? "",
        roleCode: isDirectResponsibility ? "" : displayCode(roleNode?.data?.code),
        roleName: isDirectResponsibility ? "" : roleNode?.data?.name ?? "",
        positionCode: displayCode(posNode?.data?.code),
        positionName: posNode?.data?.name ?? "",
      };

      const currentEmployee = this.employeeInfo;
      const employeeIdToUse = currentEmployee?.id || emp_id;
      if (employeeIdToUse) {
        this.evalModalEmpState = {
          id: employeeIdToUse,
          firstName: currentEmployeeData?.first_name || "",
          lastName: currentEmployeeData?.last_name || "",
          position: posNode?.data?.name ?? currentEmployeeData?.primary_position?.name ?? "",
          photoUrl: currentEmployeeData?.avatar || "",
          availability: currentEmployee?.availability ?? 1,
          isAllocated: false,
          currentEmployeeBadge: item?.data?.data?.badge || null,
          access: this.resolvePathNotifyAccess(
            this.employeeData,
            currentEmployeeData,
            currentEmployee
          ),
        };
      }

      await this.loadResponsibilityCheckpoints();
      this.isInspectResModal = true;
    },
    setupForceBadgeScenario(item, emp_id) {
      const exlAppData = JSON.parse(localStorage.getItem("exlAppData"));
      const currentEmployeeData =
        this.$store.getters["employeeStore/currentEmployee"] || this.employeeInfo;
      const employees = exlAppData?.employees;

      let currentEmployee = Array.isArray(employees)
        ? employees.find((e) => e.id === emp_id)
        : null;

      if (!currentEmployee && currentEmployeeData) {
        currentEmployee = {
          id: currentEmployeeData.id || emp_id,
          title:
            currentEmployeeData.primary_position?.name ||
            currentEmployeeData.title ||
            currentEmployeeData.position?.name,
          availability: currentEmployeeData.availability || 1,
          access: Object.hasOwn(currentEmployeeData, "access")
            ? currentEmployeeData.access
            : undefined,
        };
      }

      if (!currentEmployee && !currentEmployeeData) {
        console.error("No employee found with ID:", emp_id);
        return;
      }

      const employeeIdToUse = currentEmployee?.id || currentEmployeeData?.id || emp_id;

      if (!employeeIdToUse) {
        console.error("❌ No employee ID available for evalModalEmpState");
        return;
      }

      this.evalModalEmpState = {
        id: employeeIdToUse,
        firstName: currentEmployeeData?.first_name || "",
        lastName: currentEmployeeData?.last_name || "",
        position: currentEmployee?.title || currentEmployeeData?.position?.name || "",
        photoUrl: currentEmployeeData?.avatar || "",
        availability: currentEmployee?.availability || 1,
        isAllocated: false,
        badgeList: this.getBadgesFromDataSource(this.dataSource),
        currentEmployeeBadge: item?.data?.data?.badge || null,
        access: this.resolvePathNotifyAccess(
          this.employeeData,
          currentEmployeeData,
          currentEmployee
        ),
      };

      this.showEmployeeIssueBadge = false;
      this.isInspectResModal = false;
      this.isForceBadge = true;
    },
    getProficiencyLevelNameFromNumber(el) {
      const level = el?.proficiency_level == null ? null : Number(el.proficiency_level);
      if (level === 1) return "Apprentice";
      if (level === 2) return "Professional";
      if (level === 3) return "Coach";
      if (level === 4) return "Master";
      return "Unknown";
    },
    updateChangeEvaluationStatus(data) {
      const status = data?.evaluation_status || data?.status || data?.inspection_status;
      if (status == "not_applicable") {
        return "Not Applicable";
      } else if (status == "passed") {
        return "Passed";
      } else if (status == "failed") {
        return "Failed";
      } else if (status == "reset") {
        return "Reset Pending Evaluation";
      } else {
        return "Pending Evaluation";
      }
    },
    async onClickForceBadge(item) {
      const responsibilityId = this.currentResponsibility?.id;
      this.loading.forceBadge = true;
      this.$loader.setAppLoading(true);

      try {
        // Get employee ID from multiple sources to ensure we have it
        const employeeId =
          this.evalModalEmpState?.id ||
          this.employeeInfo?.id ||
          this.cachedEmpId ||
          this.$route?.params?.id ||
          this.$route?.query?.employeeId ||
          localStorage.getItem("emp_id");

        if (!employeeId) {
          console.error("❌ No employee ID found for onClickForceBadge");
          this.$notifications?.error(
            "Unable to find employee ID. Please refresh the page and try again."
          );
          this.loading.forceBadge = false;
          this.$loader.setAppLoading(false);
          return;
        }

        const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
        const payload = {
          employee: employeeId,
          responsibility: responsibilityId,
          level: item.radioVal,
          forced: true,
        };
        await createProficiencyBadge(accountID, payload);
        // Then reload data (which will fetch latest employee data)
        await this.clearModalStates();
        await this.reloadDataAfterBadgeOperation();
        this.$loader.setAppLoading(false);
      } catch (error) {
        console.error("Error issuing badge:", error);
        this.clearModalStates();
        this.$loader.setAppLoading(false);
      } finally {
        this.loading.forceBadge = false;
        this.$loader.setAppLoading(false);
      }
    },
    updateResponsibilityBadge(responsibilityId, badgeLevel) {
      const respIndex = this.dataSource.findIndex(
        (item) =>
          item.data &&
          item.data.type === "responsibility" &&
          item.data.code === this.currentResponsibility?.code_str
      );

      if (respIndex > -1) {
        const badgeInfo = {
          id: Date.now(),
          issued_to: localStorage.getItem("emp_id"),
          is_current: true,
          level: badgeLevel,
        };
        const updatedItem = {
          ...this.dataSource[respIndex],
          data: {
            ...this.dataSource[respIndex].data,
            badge: badgeInfo,
          },
        };
        this.$set(this.dataSource, respIndex, updatedItem);
      }
    },
    getBadgesFromDataSource(dataSource) {
      const badgeList = [];
      dataSource.forEach((item) => {
        if (item.data && item.data.badge && item.data.type === "responsibility") {
          const badge = item.data.badge;
          const newBadge = {
            id: badge.id,
            issued_to: badge.issued_to,
            is_current: badge.is_current,
            level: badge.level,
            issued_for: {
              code: item.data.code, // Include the responsibility code for badge matching
            },
          };
          badgeList.push(newBadge);
        }
      });
      return badgeList;
    },
    async handleActivateEmployee() {
      this.$loader.setAppLoading(true);
      try {
        const empId =
          this.employeeData?.id || this.$route?.params?.id || this.$route?.query?.employeeId;
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        await activateEmployee(accountID, empId);

        // Use the same pattern as EmployeeHorizontalSideBar - fetch and set employee data
        const employeeData = await this.employeeStore.fetchCurrentEmployee(empId, true);
        if (employeeData) {
          // Normalize status field - ensure both status and current_status are set
          const normalizedEmployeeData = this.normalizeEmployeeData(employeeData);

          // Update both stores for backward compatibility (same as EmployeeHorizontalSideBar)
          this.$store.commit("employeeStore/setCurrentEmployee", normalizedEmployeeData);

          // Update Pinia store as well
          this.employeeStore.setCurrentEmployee(normalizedEmployeeData);

          // CRITICAL: Force update employeeData using Vue.set to ensure reactivity
          this.$set(this, "employeeData", { ...normalizedEmployeeData });

          // Force Vue to recognize the change immediately
          await this.$nextTick();
          this.$forceUpdate();

          // Call handleEmployeeStatusChanged to trigger reactivity and data reload
          await this.handleEmployeeStatusChanged();
        }

        this.onCloseModal();
      } catch (e) {
        console.error("Error activating employee:", e);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async reactivateEmployee() {
      try {
        this.$loader.setAppLoading(true);
        const empId =
          this.employeeData?.id || this.$route?.params?.id || this.$route?.query?.employeeId;
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        if (this.unArchiveEmployee) {
          await unArchiveEmployee(accountID, empId);
        }
        await reactivateEmployee(accountID, empId);
        // Immediately fetch fresh employee data from API to update status
        const employeeData = await this.employeeStore.fetchCurrentEmployee(empId, true);
        if (employeeData) {
          // Normalize status field - ensure both status and current_status are set
          const normalizedEmployeeData = this.normalizeEmployeeData(employeeData);

          // Update both stores for backward compatibility (same as EmployeeHorizontalSideBar)
          // Use Vue.set to ensure reactivity
          this.$store.commit("employeeStore/setCurrentEmployee", normalizedEmployeeData);

          // Update Pinia store as well
          this.employeeStore.setCurrentEmployee(normalizedEmployeeData);

          // CRITICAL: Force update employeeData using Vue.set to ensure reactivity
          this.$set(this, "employeeData", { ...normalizedEmployeeData });

          // Force Vue to recognize the change immediately
          await this.$nextTick();
          this.$forceUpdate();

          // Call handleEmployeeStatusChanged to trigger reactivity and data reload
          await this.handleEmployeeStatusChanged();
        }

        this.onCloseModal();
        this.$loader.setAppLoading(false);
      } catch (e) {
        this.$loader.setAppLoading(false);
        console.error("Error reactivating employee:", e);
      }
    },
    async onAssignPositionToEmployee(event) {
      const item = event.position;
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      this.loading.employeeAssignment = true;
      this.$loader.setAppLoading(true);
      // Clear any previous error messages
      this.errorMessage = "";
      this.assignRejectionMessage = "";
      if (!this.employeeData?.id || !item?.id) {
        this.$loader.setAppLoading(false);
        return;
      }
      try {
        const payload = {
          employee: this.employeeData.id,
          position: item.id,
          commitment: 1,
          allocation: this.equalVacancyPicker ? event.allocation : 1,
          effective_on: event.effectiveDate,
          isNotify: event.notifyEmployee,
        };
        if (this.equalVacancyPicker && event.effectiveTo) {
          payload.effective_to = event.effectiveTo;
        }

        const assignmentResponse = await addPositionAssignment(accountID, payload);

        // Employee data will be updated by sidebar component after position assignment
        await this.reloadDataAfterChanges();

        // Fix: Fetch position details to get accurate roles_count
        // item.id is the position ID, assignmentResponse.data.id is the position_assignment ID
        const positionAssignmentId = assignmentResponse?.data?.id;
        if (positionAssignmentId && item.id) {
          await this.updatePositionHasRoles(accountID, item.id, positionAssignmentId);
        }

        // Emit employeeUpdated event to notify parent component (EmployeeDetail) to refresh
        this.$emit("employeeUpdated");
        this.$notifications.success("Position assigned successfully.");
        this.onCloseModal();

        // Delayed API refresh after 2-3 seconds to ensure backend has processed the changes
        setTimeout(async () => {
          try {
            await this.reloadDataAfterChanges();
            // Also emit update event to parent
            this.$emit("employeeUpdated");
          } catch (error) {
            console.error("Error in delayed position assignment refresh:", error);
          }
        }, 2500); // 2.5 seconds delay
      } catch (e) {
        console.error("error: ", e);
        if (this.equalVacancyPicker) {
          this.assignRejectionMessage = getAssignmentRejectionMessage(e);
          return;
        }
        // Handle API error messages
        if (e.response && e.response.status === 400 && e.response.data) {
          // Check if error response is an array (as mentioned in the user's case)
          if (Array.isArray(e.response.data) && e.response.data.length > 0) {
            this.errorMessage = e.response.data[0];
            this.$notifications.error(e.response.data[0]);
          } else if (typeof e.response.data === "string") {
            this.errorMessage = e.response.data;
            this.$notifications.error(e.response.data);
          } else if (e.response.data.message) {
            this.errorMessage = e.response.data.message;
            this.$notifications.error(e.response.data.message);
          } else {
            this.errorMessage = "An error occurred while assigning the position.";
            this.$notifications?.error(this.errorMessage);
          }
        } else {
          this.errorMessage = "An error occurred while assigning the position.";
          this.$notifications?.error(this.errorMessage);
        }
      } finally {
        this.$loader.setAppLoading(false);
        this.loading.employeeAssignment = false;
      }
    },
    extractCode(str) {
      let match = str?.match(/#\d+/);
      return match ? match[0] : null;
    },
    async getEmployeeData() {
      try {
        // Get employee data from store (updated by sidebar component)
        const employeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];
        if (employeeData) {
          this.employeeData = employeeData;
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
        // Fallback to store data if API fails
        const data = await this.$store.getters["employeeStore/currentEmployee"];
        if (data) {
          this.employeeData = data;
        }
      }
    },
    async showRemoveBadge(item) {
      this.$refs.PathToReadinessTreeList.showRemoveBadge(item);
    },
    getBadgeLevelStringForDialog(level) {
      if (typeof level === "string") return level;
      const levelMap = {
        1: "Apprentice",
        2: "Professional",
        3: "Coach",
        4: "Master",
      };
      return levelMap[level] || null;
    },
    async handleIssueBadgeFromInspection(payload) {
      if (payload?.isRemoveAction) {
        await this.processRemoveBadgeFromEvalModal(payload);
        return;
      }
      const level = (payload?.value || payload?.level || "apprentice").toLowerCase();
      const grandLabel = String(payload?.grand_label || "").toLowerCase();
      const isAward = grandLabel.startsWith("award");
      if (isAward) {
        this.showAwardBadge(payload);
      } else {
        this.giveBadgeDialogLockedLevel = level;
        this.onOpenGiveBadgeDialogFromInspection({ defaultLevel: level });
      }
    },
    onOpenGiveBadgeDialogFromInspection(payload) {
      this.showGiveBadgeDialogFromInspection = true;
      this.inspectionGiveBadgeDefaultLevel = (payload?.defaultLevel || "apprentice").toLowerCase();
    },
    onGiveBadgeDialogInput(visible) {
      if (!visible) {
        this.isForceBadge = false;
        this.showGiveBadgeDialogFromInspection = false;
        this.giveBadgeDialogLockedLevel = null;
        this.employeeDataWithBadges = null;
      }
    },
    resolveGiveBadgeEmployeeId(fromInspection) {
      const evalModalId = this.evalModalEmpState?.id;
      if (fromInspection) return evalModalId;
      return (
        evalModalId ||
        this.employeeInfo?.id ||
        this.cachedEmpId ||
        this.$route?.params?.id ||
        this.$route?.query?.employeeId ||
        localStorage.getItem("emp_id")
      );
    },
    formatGiveBadgeLevelStr(level, levelId, levelMap) {
      const numeric =
        typeof levelId === "number" ? levelId : levelMap[normalizeBadgeLevel(level)] ?? null;
      if (numeric == null) {
        if (typeof level === "string") {
          return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
        }
        return "Apprentice";
      }
      return this.getBadgeLevelStringForDialog(numeric);
    },
    updateInspectionBadgeStateAfterGive(level, levelId, levelMap, createdBadgeId) {
      if (!this.evalModalEmpState) return;
      const levelStr = this.formatGiveBadgeLevelStr(level, levelId, levelMap);
      this.evalModalEmpState = {
        ...this.evalModalEmpState,
        currentEmployeeBadge: {
          id: createdBadgeId ?? this.evalModalEmpState.currentEmployeeBadge?.id ?? null,
          level: levelStr || "Apprentice",
          dot: "Good Standing",
        },
      };
    },
    async reloadInspectionAfterGiveBadge() {
      if (
        !this.isInspectResModal ||
        !this.currentResponsibility?.id ||
        !this.evalModalEmpState?.id
      ) {
        return;
      }
      await this.reloadInspectionCheckpoints(null);
      await this.loadResponsibilityCheckpoints();
    },
    async handleGiveBadgeInspectionSuccess(level, levelId, levelMap, createdBadgeId) {
      this.showGiveBadgeDialogFromInspection = false;
      this.giveBadgeDialogLockedLevel = null;
      this.updateInspectionBadgeStateAfterGive(level, levelId, levelMap, createdBadgeId);
      await this.reloadDataAfterBadgeOperation();
      await this.reloadInspectionAfterGiveBadge();
    },
    async onGiveBadgeFromDialog(payload) {
      const { level, notifyEmployee } = payload;
      const fromInspection = this.showGiveBadgeDialogFromInspection;
      const employeeId = this.resolveGiveBadgeEmployeeId(fromInspection);
      const responsibilityId = this.currentResponsibility?.id;
      this.loading.forceBadge = true;
      this.$loader.setAppLoading(true);
      try {
        if (!employeeId) {
          this.$notifications?.error(
            "Unable to find employee ID. Please refresh the page and try again."
          );
          return;
        }
        const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
        const levelMap = {
          apprentice: 1,
          proficient: 2,
          coach: 3,
          architect: 4,
        };
        const levelId = levelMap[normalizeBadgeLevel(level)] || level;
        const apiPayload = {
          employee: employeeId,
          responsibility: responsibilityId,
          level: levelId,
          forced: true,
          notify: !!notifyEmployee,
        };
        const createRes = await createProficiencyBadge(accountID, apiPayload);
        const createdBadgeId = extractCreatedProficiencyBadgeId(createRes);
        await this.$notifications?.success(`
        Badge 
        ${this.getBadgeLevelStringForDialog(level)} 
        was successfully issued to
         ${this.employeeFullName}
          for responsibility
           ${this.currentResponsibility?.name}.
           `);
        if (fromInspection) {
          await this.handleGiveBadgeInspectionSuccess(level, levelId, levelMap, createdBadgeId);
        } else {
          await this.clearModalStates();
          await this.reloadDataAfterBadgeOperation();
        }
      } catch (error) {
        console.error("Error issuing badge:", error);
        if (fromInspection) {
          this.showGiveBadgeDialogFromInspection = false;
          this.giveBadgeDialogLockedLevel = null;
        } else {
          this.clearModalStates();
        }
      } finally {
        this.loading.forceBadge = false;
        this.$loader.setAppLoading(false);
      }
    },
    onEmployeeModalBack() {
      this.isForceBadge = false;
      this.showGiveBadgeDialogFromInspection = false;
      this.giveBadgeDialogLockedLevel = null;
      this.employeeDataWithBadges = null; // Clear badge data when modal closes
    },

    onUpgradeBadgeDialogInput(visible) {
      if (!visible) this.showUpgradeBadgeDialog = false;
    },

    closeUpgradeBadgeModal() {
      this.showUpgradeBadgeDialog = false;
    },

    async onGiveBadgeFromUpgradeDialog(payload) {
      const { level, notifyEmployee } = payload;
      const employeeId =
        this.evalModalEmpState?.id ||
        this.employeeInfo?.id ||
        this.cachedEmpId ||
        this.$route?.params?.id ||
        this.$route?.query?.employeeId ||
        localStorage.getItem("emp_id");
      const responsibilityId = this.currentResponsibility?.id;
      this.$loader.show();
      this.loading.upgradeBadge = true;
      try {
        if (!employeeId) {
          this.$notifications?.error(
            "Unable to find employee ID. Please refresh the page and try again."
          );
          return;
        }
        const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
        const levelMap = {
          apprentice: 1,
          proficient: 2,
          coach: 3,
          architect: 4,
        };
        const levelId = levelMap[normalizeBadgeLevel(level)] || level;
        const apiPayload = {
          employee: employeeId,
          responsibility: responsibilityId,
          level: levelId,
          forced: true,
          notify: !!notifyEmployee,
        };
        await createProficiencyBadge(accountID, apiPayload);
        await this.$notifications?.success(`
        Badge 
        ${this.getBadgeLevelStringForDialog(level)} 
        was successfully issued to
         ${this.employeeFullName}
          for responsibility
           ${this.currentResponsibility?.name}.
           `);
        this.showUpgradeBadgeDialog = false;
        await this.reloadDataAfterBadgeOperation();
      } catch (error) {
        console.error("Error issuing upgrade badge:", error);
      } finally {
        this.$loader.hide();
        this.loading.upgradeBadge = false;
      }
    },

    clearModalStates() {
      // Clear all modal states to prevent unwanted reopening
      this.isInspectResModal = false;
      this.isForceBadge = false;
      this.showGiveBadgeDialogFromInspection = false;
      this.giveBadgeDialogLockedLevel = null;
      this.showUpgradeBadgeDialog = false;
      this.showEmployeeIssueBadge = false;
      this.showBadgeConfirmationDialog = false;
      this.showAwardBadgeModal = false;
      this.pendingAwardLevel = null;
      this.employeeDataWithBadges = null;
      this.currentSelectRes = null;
      this.currentResponsibility = null;
      this.badgeData = null;
      this.evalModalEmpState = null;
      this.evalModalHeaderState = null;
    },

    async onImportanceChanged(event) {
      // Immediately reflect the new importance value on the affected row
      // before the refetched tree data replaces it.
      const item = this.dataSource.find((item) => item.id === event?.itemId);
      if (item && item.data) {
        item.data.importanceLevel = event.newImportance;
      }

      // idea-MDP-560: cell-level skeletons, one status poll and an in-place
      // patch. Edits are never blocked while a recalculation is pending.
      if (this.skeletonLoadingEnabled) {
        this.startReadinessRecalcRefresh(event?.itemId);
        return;
      }

      // Flag off: legacy behaviour. Ignore further importance changes while the
      // previous recalculation is still running.
      if (this.isRecalculating) {
        return;
      }

      // ER-11163: backend recalc is async. Poll until the affected row's
      // readiness changes — employee-header-only updates are not enough.
      //
      // ER-11396: never cover the page with the generic full-screen loader
      // while the backend recalculates (it can take minutes and reads as an
      // infinite loading state). Show a visible processing banner instead and
      // keep the page usable while the bounded poll refreshes the readiness.
      this.isRecalculating = true;
      try {
        await this.reloadDataAfterChanges({
          afterImportanceChange: true,
          baseline: {
            employeeReadiness:
              this.employeeData?.primary_readiness ?? this.employeeData?.current_readiness ?? null,
            itemId: event?.itemId ?? null,
            itemReadiness: event?.previousReadiness ?? this.getTreeRowReadiness(event?.itemId),
          },
        });
      } catch (error) {
        console.error("Error refreshing readiness after importance change:", error);
      } finally {
        this.isRecalculating = false;
      }
    },
    getBadgeLevel(level) {
      switch (level) {
        case 1:
          return "Apprentice";
        case 2:
          return "Professional";
        case 3:
          return "Coach";
        case 4:
          return "Master";
        default:
          return "Unknown";
      }
    },
    async handlePaginationRequest(paginationData) {
      // Calculate offset from page and limit
      this.pagination.page = paginationData.page;
      this.pagination.offset = (paginationData.page - 1) * this.pagination.limit;
      this.$loader.show();
      try {
        // Preserve current expansion state before pagination
        this.preserveExpansionState();

        const empId = this.cachedEmpId || this.$route.params.id || this.$route.query.employeeId;
        const accountID =
          this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
        await this.getEmpAssignments(accountID, empId);

        // Refresh child data to show latest changes
        await this.refreshExpandedChildData(accountID, empId);

        // Force TreeList to re-render but preserve expansion state
        this.treeListKey++;

        // Restore expansion state after data is loaded
        // Use a combination of $nextTick and setTimeout for better timing
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.restoreExpansionState();
          });
        });
      } catch (error) {
        console.error("Error loading paginated data:", error);
      } finally {
        this.$loader.hide();
      }
    },

    addSkeletonLoaders(parentId, rowType, count = null) {
      // Check if skeleton loaders already exist for this parent
      const existingSkeletons = this.dataSource.filter(
        (item) => item.parentId === parentId && item.data && item.data.isSkeletonLoader
      );

      if (existingSkeletons.length > 0) {
        return; // Skip adding if already exists
      }

      // Use provided count or fallback to default values
      const skeletonCount = count || (rowType === "position" ? 2 : 3);
      const skeletonItems = [];

      for (let i = 0; i < skeletonCount; i++) {
        const skeletonId = `skeleton-${parentId}-${i}`;
        skeletonItems.push({
          id: skeletonId,
          parentId: parentId,
          data: {
            type: "skeleton",
            assignmentType: rowType === "position" ? "role" : "responsibility",
            skeletonFor: rowType,
            isSkeletonLoader: true,
            commitmentLevel: "",
            importanceLevel: 0,
            importanceSelector: false,
            totalSteps: 3,
            code: "",
            name: "",
            isNew: false,
            isAccepted: false,
            acceptanceStatus: "pending",
          },
          readiness: 0,
          status: "loading",
        });
      }

      // Add skeleton items to dataSource
      this.dataSource = [...this.dataSource, ...skeletonItems];
    },

    removeSkeletonLoaders(parentId) {
      // Remove skeleton items for this parent
      this.dataSource = this.dataSource.filter((item) => {
        const isSkeletonForParent = item.id.toString().startsWith(`skeleton-${parentId}-`);
        const isSkeletonLoader = item.data && item.data.isSkeletonLoader;
        const hasCorrectParent = item.parentId === parentId;

        // Remove if it's a skeleton loader for this parent
        return !(isSkeletonForParent && isSkeletonLoader && hasCorrectParent);
      });
    },

    // Helper method to extract original ID from prefixed ID
    getOriginalId(prefixedId) {
      if (typeof prefixedId === "string") {
        if (prefixedId.startsWith("position_")) return prefixedId.replace("position_", "");
        if (prefixedId.startsWith("role_")) return prefixedId.replace("role_", "");
        if (prefixedId.startsWith("responsibility_"))
          return prefixedId.replace("responsibility_", "");
      }
      return prefixedId;
    },

    // Expansion state management methods
    preserveExpansionState() {
      try {
        const treeListRef =
          this.$refs &&
          this.$refs.PathToReadinessTreeList &&
          this.$refs.PathToReadinessTreeList.$refs &&
          this.$refs.PathToReadinessTreeList.$refs.treelist;
        if (treeListRef && treeListRef.instance) {
          const treeInstance = treeListRef.instance;
          const currentExpandedKeys = treeInstance.option("expandedRowKeys") || [];
          this.expandedKeys = [...currentExpandedKeys];
        }
        // Keep existing expanded keys if reference not found
      } catch (error) {
        console.warn("Could not preserve expansion state:", error);
        // Don't reset expandedKeys here, keep what we have
      }
    },

    restoreExpansionState() {
      try {
        if (this.expandedKeys.length > 0) {
          // Force reactivity by creating a new array reference
          this.expandedKeys = [...this.expandedKeys];
        }
      } catch (error) {
        console.warn("Could not restore expansion state:", error);
      }
    },

    handleExpansionStateChange(expandedKeys) {
      // Update our local copy of expanded keys when user manually expands/collapses
      this.expandedKeys = expandedKeys || [];
    },

    async refreshExpandedChildData(accountID, empId, { manageLoading = true } = {}) {
      // Get all expanded keys to know what child data to refresh
      const expandedKeys = this.expandedKeys || [];
      if (expandedKeys.length === 0) {
        return;
      }

      if (manageLoading) {
        this.loading.readinessBar = true;
      }

      try {
        // Clear the cache so we can reload fresh data
        this.loadedChildData.clear();

        // Remove all child items from dataSource so they can be reloaded with fresh data
        this.dataSource = this.dataSource.filter((item) => item.parentId === -1);

        // Reload child data for each expanded item
        for (const expandedKey of expandedKeys) {
          const expandedItem = this.dataSource.find((item) => item.id === expandedKey);
          if (!expandedItem) continue;

          try {
            if (expandedItem.data && expandedItem.data.type === "position") {
              // Get the numeric ID from the prefixed ID
              const positionAssignmentId = expandedKey.replace("position_", "");
              await this.handlePositionExpansion(accountID, empId, positionAssignmentId);
            } else if (expandedItem.data && expandedItem.data.type === "role") {
              // Get the numeric ID from the prefixed ID
              const roleAssignmentId = expandedKey.replace("role_", "");
              await this.handleRoleExpansion(accountID, empId, roleAssignmentId, expandedItem);
            }
          } catch (error) {
            console.error(`Error refreshing data for ${expandedKey}:`, error);
          }
        }
      } finally {
        if (manageLoading) {
          this.loading.readinessBar = false;
        }
      }
    },

    async handleInspectionBadgeExpirationSynced() {
      try {
        this.$loader.show();
        try {
          await this.refreshExpandedChildData(this.cachedAccountID, this.cachedEmpId);
        } finally {
          this.$loader.hide();
        }
        const empId = this.evalModalEmpState?.id || this.cachedEmpId;
        const respId = this.currentResponsibility?.id;
        if (empId && respId) {
          await this.syncInspectionPaneBadgeFromApi(empId, respId);
        }
        if (
          this.isInspectResModal &&
          this.currentResponsibility?.id &&
          this.evalModalEmpState?.id
        ) {
          await this.reloadInspectionCheckpoints(null);
          await this.loadResponsibilityCheckpoints();
        }
      } catch (error) {
        console.error("Error refreshing after badge expiration sync:", error);
      }
    },

    async handleBadgeAwarded(payload) {
      const isObj = payload && typeof payload === "object";
      try {
        if (isObj && this.isInspectResModal && this.evalModalEmpState) {
          this.evalModalEmpState = {
            ...this.evalModalEmpState,
            currentEmployeeBadge: {
              id: payload.badgeId ?? this.evalModalEmpState.currentEmployeeBadge?.id ?? null,
              level: payload.level ?? this.evalModalEmpState.currentEmployeeBadge?.level,
              dot:
                payload.dot ?? this.evalModalEmpState.currentEmployeeBadge?.dot ?? "Good Standing",
            },
          };
        }
        await this.reloadDataAfterBadgeOperation();

        const empId = isObj ? payload.employeeId : this.evalModalEmpState?.id || this.cachedEmpId;
        const respId =
          isObj && payload.responsibilityId
            ? payload.responsibilityId
            : this.currentResponsibility?.id;
        if (empId && respId) {
          await this.syncInspectionPaneBadgeFromApi(empId, respId);
        }
        if (
          this.isInspectResModal &&
          this.currentResponsibility?.id &&
          this.evalModalEmpState?.id
        ) {
          await this.reloadInspectionCheckpoints(null);
          await this.loadResponsibilityCheckpoints();
        }
      } catch (error) {
        console.error("Error refreshing after badge award:", error);
      }
    },
  },
};
</script>
<style>
.custom-path-readiness {
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280d;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.readiness-title {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
  color: #344054;
}

.custom-readiness {
  width: 100%;
  margin-left: 15px;
}

.custom-readiness .progress-bar-container {
  width: 100%;
}

.custom-readiness .progress-value {
  font-weight: 600;
}

.custom-path {
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280d;
  border-radius: 8px;
  width: 100%;
  overflow: visible;
}

.path-to-readiness-scrollable {
  min-height: 0;
}

.path-to-readiness-scrollable ::v-deep .ex-pathtoread-treelist {
  min-height: 0;
}

.path-to-readiness-scrollable ::v-deep .dx-treelist {
  min-height: 0;
}

.path-to-readiness-scrollable ::v-deep .dx-scrollable-wrapper {
  min-height: 0;
}

.path-to-readiness-scrollable ::v-deep .dx-scrollable-container {
  min-height: 0;
}

/* Ensure parent containers allow scrolling */
::v-deep .employee-card-path-to-readiness {
  overflow: visible !important;
  height: auto !important;
  max-height: none !important;
}

::v-deep .employee-card-path-to-readiness .custom-path {
  overflow: visible !important;
  height: auto !important;
  max-height: none !important;
}

::v-deep .employee-card-path-to-readiness .custom-path .ex-pathtoread-treelist {
  overflow: visible !important;
  height: auto !important;
  max-height: none !important;
}

::v-deep .employee-card-path-to-readiness .custom-path .ex-pathtoread-treelist .dx-treelist {
  height: auto !important;
  max-height: none !important;
}

::v-deep
  .employee-card-path-to-readiness
  .custom-path
  .ex-pathtoread-treelist
  .dx-treelist-rowsview {
  /* Remove scrolling from table - always scroll page instead */
  overflow: visible !important;
  height: auto !important;
  max-height: none !important;
  overscroll-behavior: none;
}
</style>
<style scoped>
::v-deep .employee-card-header-chatter-link:hover {
  margin-top: 0;
}

/* Make the readiness bar wider like in new organization employee detail */
::v-deep .employee-card-path-to-readiness .employee-page-header .employee-readiness-section {
  flex: 1 1 auto;
  min-width: 400px;
  max-width: 600px;
  margin: 0 30px;
}

::v-deep
  .employee-card-path-to-readiness
  .employee-page-header
  .employee-readiness-section
  .ex-employee-readiness-box {
  width: 100%;
}

::v-deep .minimalistic-readiness-level.Coach {
  border: 1px solid #00a3ff;
  background: #effeff;
}

::v-deep .minimalistic-readiness-level.Master {
  border: 1px solid #2e1ec1;
  background: #f2f3ff;
}

::v-deep .minimalistic-readiness-level.Apprentice {
  border: 1px solid #c17200;
  background: #fff5d3;
}

::v-deep .minimalistic-readiness-level.Professional {
  border: 1px solid #00b813;
  background: #f3fff9;
}

/* ER-11396: visible "processing" state shown while readiness is being
   recalculated after an importance change (replaces the full-page loader). */
.readiness-recalc-status {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  padding: 10px 14px;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  background: #f4f1ff;
  color: #1d1c1d;
  font-size: 14px;
  line-height: 1.4;
}

.readiness-recalc-status__spinner {
  flex: 0 0 auto;
}

.readiness-recalc-status__text {
  color: #4a4458;
}
</style>
